import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
import { 
  FolderOpen, Play, Plus, Trash2, FileText, CheckCircle, AlertTriangle, XCircle, 
  ArrowLeft, Calculator, Video, MapPin, Home, LogOut, RefreshCw, Settings, Shield, MessageSquare,
  Image as ImageIcon, Download, File as FileIcon, Mail, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../context/AuthContext';
import { expertApi, projectsApi } from '../services/api';
import { toast } from 'sonner';
import ProjectCommunication from '../components/ProjectCommunication';
import { useUnreadMessages, UnreadDot } from '../lib/useUnreadMessages';
import ProjectMiniMap from '../components/ProjectMiniMap';

const WORK_PRESETS = [
  { id: 'elec', label: 'Électricité', defaultPrice: 4000, unit: 'forfait' },
  { id: 'plomb', label: 'Plomberie/SDB', defaultPrice: 3500, unit: 'forfait' },
  { id: 'sol', label: 'Sols', defaultPrice: 50, unit: 'm²' },
  { id: 'peinture', label: 'Peinture', defaultPrice: 35, unit: 'm²' },
  { id: 'cuisine', label: 'Cuisine', defaultPrice: 5000, unit: 'forfait' },
  { id: 'menuiserie', label: 'Menuiseries/Fenêtres', defaultPrice: 800, unit: 'unité' },
  { id: 'toiture', label: 'Toiture', defaultPrice: 150, unit: 'm²' },
  { id: 'facade', label: 'Façade/Ravalement', defaultPrice: 80, unit: 'm²' },
  { id: 'isolation', label: 'Isolation', defaultPrice: 60, unit: 'm²' },
  { id: 'chauffage', label: 'Chauffage', defaultPrice: 4500, unit: 'forfait' },
  { id: 'demolition', label: 'Démolition', defaultPrice: 2000, unit: 'forfait' },
  { id: 'maconnerie', label: 'Maçonnerie', defaultPrice: 3000, unit: 'forfait' },
];

const NOTARY_RATES = [
  { id: 'standard', label: 'Standard (8%)', rate: 0.08 },
  { id: 'marchand', label: 'Marchand (2.5%)', rate: 0.025 },
  { id: 'encheres', label: 'Enchères (20%)', rate: 0.20 },
];

const VIDEO_LABELS = {
  structure: 'Sols & Plafonds',
  electricite: 'Électricité',
  plomberie: 'Plomberie',
  menuiseries: 'Menuiseries',
  commentaire: 'Note Vocale',
};

const DOC_LABELS = {
  doc_dpe: 'DPE / Diagnostic Énergétique',
  doc_taxe_fonciere: 'Taxe Foncière',
  doc_plans: 'Plans / Cadastre',
  doc_autres_docs: 'Autre document',
};

const PHOTO_LABELS = {
  photo_facade: 'Façade / Extérieur',
  photo_pieces: 'Pièce principale',
  photo_defauts: 'Défaut visible',
  photo_autres_photos: 'Autre photo',
};

// Determine display kind (image / video / document) from a media record
const _getMediaKind = (m) => {
  const ct = (m.content_type || '').toLowerCase();
  const name = (m.original_filename || '').toLowerCase();
  if (ct.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif)$/.test(name)) return 'image';
  if (ct.startsWith('video/') || /\.(mp4|mov|avi|webm|mkv)$/.test(name)) return 'video';
  if (ct.startsWith('audio/') || /\.(mp3|wav|m4a|ogg|aac)$/.test(name)) return 'audio';
  // Fallback: infer from type prefix
  const t = (m.type || '').toLowerCase();
  if (t.startsWith('photo_')) return 'image';
  if (t.startsWith('doc_')) return 'document';
  if (['structure', 'electricite', 'plomberie', 'menuiseries', 'commentaire'].some(v => t.startsWith(v))) return 'video';
  if (t.startsWith('autres_videos')) return 'video';
  return 'document';
};

const _getMediaLabel = (m) => {
  const t = (m.type || '').toLowerCase();
  // Strip trailing _0/_1 index suffix
  const base = t.replace(/_\d+$/, '');
  if (VIDEO_LABELS[base]) return VIDEO_LABELS[base];
  if (DOC_LABELS[base]) return DOC_LABELS[base];
  if (PHOTO_LABELS[base]) return PHOTO_LABELS[base];
  if (base.startsWith('autres_videos')) return 'Autre vidéo';
  return m.original_filename || base || 'Fichier';
};

export default function ExpertDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  // Mode partenaire : priorité au partner_token s'il existe (même si un token client traîne)
  const partnerToken = localStorage.getItem('partner_token');
  const partnerRawData = localStorage.getItem('partner_data');
  const isPartnerMode = !!partnerToken;
  const partnerSelf = isPartnerMode && partnerRawData ? (() => { try { return JSON.parse(partnerRawData); } catch { return null; } })() : null;

  // Auth header actif selon le mode
  const activeToken = isPartnerMode ? partnerToken : token;

  // Lecture du filtre partenaire : auto si mode partenaire, sinon depuis l'URL
  const urlPartner = new URLSearchParams(window.location.search).get('partner');
  const partnerFilter = isPartnerMode ? (partnerSelf?.subdomain || null) : urlPartner;

  // Inject correct auth header for axios when in partner mode
  React.useEffect(() => {
    if (isPartnerMode && partnerToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${partnerToken}`;
    }
    return () => {
      if (isPartnerMode) {
        const regularToken = localStorage.getItem('token');
        if (regularToken) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${regularToken}`;
        }
      }
    };
  }, [isPartnerMode, partnerToken]);

  const [projects, setProjects] = useState([]);
  const { unread: unreadByProject, refresh: refreshUnread } = useUnreadMessages(projects, activeToken);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const mainScrollRef = useRef(null);
  
  // Calculator state
  const [notaryRate, setNotaryRate] = useState(0.08);
  const [workEstimates, setWorkEstimates] = useState([]);
  const [customWork, setCustomWork] = useState({ label: '', priceUnit: '', quantity: '', unit: 'forfait', description: '' });
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [resalePrice, setResalePrice] = useState('');
  const [expertNotes, setExpertNotes] = useState('');
  const [selectedScore, setSelectedScore] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [editingWorkIndex, setEditingWorkIndex] = useState(null);
  const [sendingDevis, setSendingDevis] = useState(false);
  const [includeFiches, setIncludeFiches] = useState(true);
  // Phase 1 — Alertes réglementaires
  const [dpeClass, setDpeClass] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [showDpeHint, setShowDpeHint] = useState(false);
  const [regCheck, setRegCheck] = useState(null);
  const [regLoading, setRegLoading] = useState(false);
  // Phase 2 — Scénarios financiers
  const [rentalPriceM2, setRentalPriceM2] = useState('');
  const [mdbAcquiredWithTva, setMdbAcquiredWithTva] = useState(true);
  const [loanRate, setLoanRate] = useState(4.2);
  const [loanDuration, setLoanDuration] = useState(20);
  const [loanLtv, setLoanLtv] = useState(80);
  const [scenarios, setScenarios] = useState(null);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  // Phase 3/4/5 — Enrichissement
  const [incomeTier, setIncomeTier] = useState('violet');
  const [householdSize, setHouseholdSize] = useState(2);
  const [dpeAfter, setDpeAfter] = useState('C');
  const [isCopro, setIsCopro] = useState(false);
  const [hasElecWork, setHasElecWork] = useState(true);
  const [enrichment, setEnrichment] = useState(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [downloadingAuditPdf, setDownloadingAuditPdf] = useState(false);
  // Mobile responsive: toggle between list and detail
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  // Filtre partenaire (admin mode uniquement)
  const [partnersList, setPartnersList] = useState([]);
  const [activePartnerFilter, setActivePartnerFilter] = useState(urlPartner || '');

  // === ESTIMATION MARCHÉ DVF (affichée dans le Bilan Financier) ===
  const [marketEstimation, setMarketEstimation] = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [terrainAdjustment, setTerrainAdjustment] = useState('');
  
  // === AJUSTEMENTS EXPERT ===
  const [expertAdjustments, setExpertAdjustments] = useState([]);
  const [newAdjustment, setNewAdjustment] = useState({ label: '', amount: '', type: 'valorisation' });
  const [savingAdjustments, setSavingAdjustments] = useState(false);

  // Fetch latest loan rate from admin config on mount
  useEffect(() => {
    const fetchLoanRate = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/loan-rates`);
        if (res.data && res.data.rate_20y) {
          setLoanRate(res.data.rate_20y);
        }
      } catch (e) { /* silent */ }
    };
    fetchLoanRate();
  }, []);

  // Charger la liste des partenaires (admin uniquement)
  useEffect(() => {
    if (isPartnerMode) return;
    const fetchPartners = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/partenaires/subscriptions`, {
          headers: { Authorization: `Bearer ${activeToken}` }
        });
        setPartnersList(res.data || []);
      } catch (e) { /* silent */ }
    };
    fetchPartners();
  }, [isPartnerMode, activeToken]);
  
  // Assurances Marchand (auto-calculées)
  const [insurancesDO, setInsurancesDO] = useState(0); // Dommage-Ouvrage 2%
  const [insurancesRCPro, setInsurancesRCPro] = useState(500); // RC Pro forfait
  const [bankFees, setBankFees] = useState(0); // Frais bancaires 1%
  const [gfaFees, setGfaFees] = useState(0); // GFA si VEFA
  const [showMarchandComparison, setShowMarchandComparison] = useState(false);
  // Profil Fiscal explicite (sélectionnable par l'expert)
  const [fiscalProfile, setFiscalProfile] = useState('particulier'); // particulier | sci_ir | sci_is | marchand_biens

  // === AUDIT FISCAL OPTIMISÉ — données complétables a posteriori par l'expert ===
  const [purchaseYear, setPurchaseYear] = useState('');
  const [historicalPrice, setHistoricalPrice] = useState('');
  const [constructionYear, setConstructionYear] = useState('');
  const [isResidencePrincipale, setIsResidencePrincipale] = useState(false);
  const [savingFiscal, setSavingFiscal] = useState(false);

  const saveFiscalOptim = async () => {
    if (!selectedProject) return;
    setSavingFiscal(true);
    try {
      const payload = {
        purchase_year: purchaseYear ? parseInt(purchaseYear) : null,
        historical_purchase_price: historicalPrice ? parseFloat(historicalPrice) : null,
        construction_year: constructionYear ? parseInt(constructionYear) : null,
        is_residence_principale: !!isResidencePrincipale,
      };
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${selectedProject.id}`, payload);
      toast.success('Audit fiscal complété ✓ Le PDF appliquera maintenant les abattements.');
      fetchProjects();
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingFiscal(false);
    }
  };

  // Fonction pour générer les notes par défaut selon le score
  // overrideMarket : passer null pour forcer "À définir" (évite d'utiliser un marketEstimation périmé d'un ancien projet)
  const generateDefaultNotes = (score, marginValue, marginPct, project = selectedProject, overrideMarket = undefined) => {
    const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
    const address = project?.address || 'Adresse du bien';
    const surface = project?.surface || 0;
    const purchasePrice = project?.purchase_price || 0;
    const pricePerSqm = surface > 0 ? Math.round(purchasePrice / surface) : 0;
    const totalWorksAmount = workEstimates.reduce((sum, w) => sum + (w.amount || w.total || 0), 0);

    // Calcul bilan financier complet
    const notaryFees = purchasePrice * notaryRate;
    const financialFees = purchasePrice * 0.01;
    const totalCost = purchasePrice + notaryFees + totalWorksAmount + financialFees;
    // Utiliser overrideMarket si fourni explicitement (null = pas d'estimation), sinon l'état courant
    const activeMarket = overrideMarket !== undefined ? overrideMarket : marketEstimation;
    const effectiveResale = resalePrice ? parseFloat(resalePrice) : (activeMarket?.estimated_price || 0);

    // Section bilan financier (commune aux 3 templates)
    const worksDetail = workEstimates.length > 0
      ? workEstimates.map(w => `    • ${w.label || w.type || 'Poste'} : ${fmt(w.amount || 0)}`).join('\n')
      : '    (Aucun poste renseigné)';

    const bilanFinancier = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                    BILAN FINANCIER PRÉVISIONNEL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  Prix d'acquisition      : ${fmt(purchasePrice)}
  Frais de notaire (${(notaryRate * 100).toFixed(1)}%)  : ${fmt(notaryFees)}
  Budget travaux          : ${totalWorksAmount > 0 ? fmt(totalWorksAmount) : 'Non chiffré'}
${totalWorksAmount > 0 ? worksDetail + '\n' : ''}  Frais financiers (1%)   : ${fmt(financialFees)}
  ─────────────────────────────────────────────────
  COÛT TOTAL OPÉRATION    : ${fmt(totalCost)}
${effectiveResale > 0
  ? `  Prix de revente estimé  : ${fmt(effectiveResale)}
  MARGE NETTE             : ${fmt(marginValue)} (${marginPct}%)`
  : `  Prix de revente         : À définir (non renseigné)
  MARGE NETTE             : Calcul en attente du prix de revente`}
`;

    const templates = {
      green: `══════════════════════════════════════════════════════════════
                    RAPPORT D'EXPERTISE IMMOBILIÈRE
                         AVIS FAVORABLE ✅
══════════════════════════════════════════════════════════════

OBJET : Analyse de faisabilité économique pour acquisition immobilière
BIEN   : ${address}
SURFACE: ${surface} m²  |  ${pricePerSqm.toLocaleString('fr-FR')} €/m²
DATE   : ${new Date().toLocaleDateString('fr-FR')}
${bilanFinancier}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         NIVEAU DE RISQUE : FAIBLE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Après analyse approfondie du dossier et étude des éléments techniques et financiers, je valide cette opération immobilière.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         POINTS FORTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

✓ Rentabilité satisfaisante avec une marge de ${marginPct}% supérieure au seuil de sécurité de 8%
✓ Budget travaux maîtrisé et cohérent avec l'état du bien
✓ Prix d'acquisition en adéquation avec le marché local
✓ Potentiel de valorisation confirmé par les transactions DVF récentes
✓ Emplacement favorable à la revente

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         ANALYSE TECHNIQUE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${totalWorksAmount > 0
  ? `Le bien présente un état général compatible avec le budget travaux estimé. Les diagnostics techniques n'ont pas révélé de désordres majeurs nécessitant des travaux structurels imprévus.

L'enveloppe travaux de ${fmt(totalWorksAmount)} intègre une marge de sécurité de 10% pour aléas de chantier, conformément aux bonnes pratiques du métier.`
  : `Le bien ne nécessite pas de travaux immédiats selon les éléments transmis. Un état des lieux technique complet (diagnostics COFRAC : DPE, Amiante, Plomb, Élec, Gaz, ERP, Termites, Carrez) reste à effectuer avant compromis pour confirmation définitive de l'état du bien.`}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         RECOMMANDATION FINALE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

✅ FEU VERT — INVESTISSEMENT RECOMMANDÉ

Cette opération présente un profil risque/rentabilité favorable. Je recommande de sécuriser rapidement ce bien avant qu'un concurrent ne se positionne.

Conditions de succès :
• Signer le compromis dans les 15 jours
• Obtenir le financement bancaire sous 45 jours
• Planifier les travaux pour une livraison sous 4-6 mois

══════════════════════════════════════════════════════════════
Expert Immobilier — L'Œil du Marchand
25 ans d'expérience terrain en bâtiment et transactions immobilières
══════════════════════════════════════════════════════════════`,

      orange: `══════════════════════════════════════════════════════════════
                    RAPPORT D'EXPERTISE IMMOBILIÈRE
                         AVIS RÉSERVÉ ⚠️
══════════════════════════════════════════════════════════════

OBJET : Analyse de faisabilité économique pour acquisition immobilière
BIEN   : ${address}
SURFACE: ${surface} m²  |  ${pricePerSqm.toLocaleString('fr-FR')} €/m²
DATE   : ${new Date().toLocaleDateString('fr-FR')}
${bilanFinancier}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         NIVEAU DE RISQUE : MODÉRÉ
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Après analyse approfondie du dossier, ce projet présente des éléments positifs mais nécessite des ajustements avant validation définitive.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         POINTS DE VIGILANCE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

⚠ La marge de ${marginPct}% est en dessous du seuil de confort habituel (8-10%)
⚠ Le budget travaux nécessite une validation par devis détaillés
⚠ Le prix d'acquisition pourrait être négocié à la baisse
⚠ Conditions de revente à vérifier sur ce secteur géographique

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         ANALYSE DES RISQUES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

RISQUE FINANCIER : La marge prévisionnelle laisse peu de place aux imprévus. Un dépassement de budget de 15% annulerait la rentabilité de l'opération.

RISQUE TECHNIQUE : ${totalWorksAmount > 0 ? "Certains postes de travaux nécessitent une expertise complémentaire avant engagement." : "Le budget travaux n'est pas encore chiffré. Il est fortement recommandé de faire établir des devis détaillés par des artisans qualifiés avant signature du compromis, afin de valider la faisabilité financière de l'opération."}

RISQUE MARCHÉ : Les délais de revente sur ce secteur peuvent être supérieurs à la moyenne.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         RECOMMANDATION FINALE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

⚠️ PROJET CONDITIONNEL — POINTS À CLARIFIER AVANT ENGAGEMENT

Cette opération peut devenir rentable sous réserve de :

1. Négocier le prix d'achat de 5 à 10%
2. Obtenir des devis fermes pour les travaux principaux
3. Valider le prix de revente par une étude de marché locale
4. Prévoir une réserve financière de 15% minimum

Je reste disponible pour accompagner la négociation et sécuriser cette opération.

══════════════════════════════════════════════════════════════
Expert Immobilier — L'Œil du Marchand
25 ans d'expérience terrain en bâtiment et transactions immobilières
══════════════════════════════════════════════════════════════`,

      red: `══════════════════════════════════════════════════════════════
                    RAPPORT D'EXPERTISE IMMOBILIÈRE
                         AVIS DÉFAVORABLE ❌
══════════════════════════════════════════════════════════════

OBJET : Analyse de faisabilité économique pour acquisition immobilière
BIEN   : ${address}
SURFACE: ${surface} m²  |  ${pricePerSqm.toLocaleString('fr-FR')} €/m²
DATE   : ${new Date().toLocaleDateString('fr-FR')}
${bilanFinancier}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         NIVEAU DE RISQUE : ÉLEVÉ
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Après étude approfondie du dossier, je ne valide pas cette opération en l'état. Les risques identifiés sont trop importants par rapport au rendement attendu.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         PROBLÈMES IDENTIFIÉS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

✗ Marge insuffisante (${marginPct}%) pour couvrir les aléas de chantier
✗ Prix d'acquisition surévalué par rapport au marché local
✗ Budget travaux sous-estimé au regard de l'état réel du bien
✗ Conditions de marché défavorables sur ce secteur
✗ Risque de perte financière en cas d'imprévu

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         ANALYSE DES RISQUES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

RISQUE FINANCIER CRITIQUE : Avec une marge de ${marginPct}%, le moindre imprévu (dépassement travaux de 10%, délai de revente prolongé) entraînerait une perte financière.

RISQUE TECHNIQUE : ${totalWorksAmount > 0 ? "L'état du bien nécessite probablement des travaux plus importants que prévu initialement." : "Aucun devis travaux n'a été fourni à ce stade. Une visite technique approfondie et l'établissement de devis fermes sont indispensables avant tout engagement, pour identifier les postes de rénovation nécessaires (structure, toiture, réseaux, isolation) et leur coût réel."}

RISQUE MARCHÉ : Les données DVF du quartier ne confirment pas le prix de revente envisagé.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                         RECOMMANDATION FINALE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

❌ FEU ROUGE — INVESTISSEMENT NON RECOMMANDÉ

Je déconseille formellement cette opération. Les conditions actuelles ne permettent pas de garantir la rentabilité de l'investissement.

Mon conseil professionnel :
• NE PAS signer le compromis en l'état
• Renégocier fortement le prix d'achat (minimum -15%)
• Ou renoncer à cette affaire et chercher un bien avec un meilleur potentiel

Le marché immobilier offre d'autres opportunités avec un meilleur rapport risque/rendement. Je reste à votre disposition pour étudier d'autres biens.

══════════════════════════════════════════════════════════════
Expert Immobilier — L'Œil du Marchand
25 ans d'expérience terrain en bâtiment et transactions immobilières
══════════════════════════════════════════════════════════════`
    };

    return templates[score] || '';
  };

  const handleScoreChange = (score) => {
    setSelectedScore(score);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      const currentNotaryRate = selectedProject.notary_rate || 0.08;
      const currentWorkEstimates = selectedProject.work_estimates || [];
      setNotaryRate(currentNotaryRate);
      setWorkEstimates(currentWorkEstimates);
      setResalePrice('');

      // Auto-remplissage des notes si vides mais score existant
      const existingNotes = selectedProject.expert_notes || '';
      if (!existingNotes && selectedProject.expert_score) {
        // Passer null pour overrideMarket : évite d'utiliser le marketEstimation périmé
        // du projet précédemment sélectionné (race condition entre useEffect et setState)
        setExpertNotes(generateDefaultNotes(selectedProject.expert_score, 0, '0', selectedProject, null));
      } else {
        setExpertNotes(existingNotes);
      }

      setExpertAdjustments(selectedProject.expert_adjustments || []);
      
      // Si le dossier n'a pas encore été validé, validation automatique silencieuse
      if (!selectedProject.expert_score) {
        autoValidateProject();
      } else {
        setSelectedScore(selectedProject.expert_score);
      }
      
      // Initialise le profil fiscal depuis le dossier (rétro-compat : 'marchand' → 'marchand_biens')
      const rawProfile = selectedProject.fiscal_profile || 'particulier';
      const normalized = rawProfile === 'marchand' ? 'marchand_biens' : rawProfile;
      setFiscalProfile(['particulier', 'sci_ir', 'sci_is', 'marchand_biens'].includes(normalized) ? normalized : 'particulier');
      // Audit fiscal optimisé
      setPurchaseYear(selectedProject.purchase_year || '');
      setHistoricalPrice(selectedProject.historical_purchase_price || '');
      setConstructionYear(selectedProject.construction_year || '');
      setIsResidencePrincipale(!!selectedProject.is_residence_principale);
      // Ajustement terrain expert
      setTerrainAdjustment(selectedProject.expert_terrain_adjustment != null ? String(selectedProject.expert_terrain_adjustment) : '');
      // Charger l'estimation marché DVF
      setMarketEstimation(null);
      setMarketLoading(true);
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expert/projects/${selectedProject.id}/market-estimation`, {
        headers: { Authorization: `Bearer ${activeToken || localStorage.getItem('token')}` }
      })
        .then(res => setMarketEstimation(res.data))
        .catch(() => setMarketEstimation({ available: false, reason: 'Erreur' }))
        .finally(() => setMarketLoading(false));
    }
  }, [selectedProject]);

  const autoValidateProject = async () => {
    if (!selectedProject) return;
    
    try {
      const purchasePrice = selectedProject.purchase_price || 0;
      const surface = selectedProject.surface || 80;
      
      // Étape 1 : Essayer l'API DVF en temps réel
      let estimatedResale = 0;
      const authToken = activeToken || localStorage.getItem('token');
      try {
        const dvfResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/expert/projects/${selectedProject.id}/market-estimation`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (dvfResponse.data && dvfResponse.data.available) {
          estimatedResale = Math.round(dvfResponse.data.estimated_price || 0);
        }
      } catch (error) {
        console.error('Error fetching DVF data:', error);
      }
      
      // Étape 2 : Fallback sur les données DVF stockées dans le dossier
      if (estimatedResale === 0 && selectedProject.dvf_average_price_m2 > 0 && surface > 0) {
        estimatedResale = Math.round(selectedProject.dvf_average_price_m2 * surface);
      }
      
      // Calculer la marge
      const totalWorks = (selectedProject.work_estimates || []).reduce((s, w) => s + (w.amount || 0), 0);
      const notaryFees = purchasePrice * 0.08;
      const totalCost = purchasePrice + notaryFees + totalWorks;
      const margin = estimatedResale - totalCost;
      const marginPct = totalCost > 0 ? (margin / totalCost) * 100 : 0;
      
      // Déterminer le score selon la logique DVF
      let autoScore = 'orange'; // Neutre par défaut
      
      if (estimatedResale > 0) {
        if (totalCost <= estimatedResale) {
          const ecartPct = ((estimatedResale - totalCost) / estimatedResale) * 100;
          if (ecartPct >= 15) {
            autoScore = 'green';
          } else if (ecartPct >= 5) {
            autoScore = 'orange';
          } else {
            autoScore = 'red';
          }
        } else {
          // Prix au-dessus du marché → REFUSÉ
          autoScore = 'red';
        }
      } else {
        // Étape 3 : Pas de données DVF — utiliser net_margin si disponible
        const storedNetMargin = selectedProject.net_margin;
        if (storedNetMargin !== null && storedNetMargin !== undefined) {
          if (storedNetMargin < -10000) autoScore = 'red';
          else if (storedNetMargin > 15000) autoScore = 'green';
          // Sinon reste 'orange'
        }
      }
      
      // Valider automatiquement le projet
      const data = {
        score: autoScore,
        notes: '',
        // Ne pas sauvegarder estimated_resale_price — c'est un calcul temporaire
        // L'expert saisira sa propre estimation si nécessaire
        notary_rate: 0.08,
        work_estimates: selectedProject.work_estimates || [],
        fiscal_profile: selectedProject.fiscal_profile || 'particulier',
      };
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/expert/projects/${selectedProject.id}/calculate`,
        data,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      
      // Recharger la liste des projets pour afficher le score calculé
      await fetchProjects();
      
    } catch (error) {
      console.error('Auto-validation error:', error);
    }
  };

  const fetchProjects = async (filterOverride) => {
    try {
      const filter = filterOverride !== undefined ? filterOverride : (isPartnerMode ? partnerFilter : activePartnerFilter);
      // Passer le token explicitement pour éviter la race condition avec AuthContext.logout()
      const currentToken = isPartnerMode ? partnerToken : (token || localStorage.getItem('token'));
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/expert/projects${filter ? `?partner=${encodeURIComponent(filter)}` : ''}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${currentToken}` } });
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Erreur lors du chargement des dossiers');
    } finally {
      setLoading(false);
    }
  };

  const addPresetWork = (preset) => {
    const quantity = preset.unit === 'm²' ? (selectedProject?.surface || 1) : 1;
    const total = preset.defaultPrice * quantity;
    
    setWorkEstimates([...workEstimates, {
      label: preset.label,
      priceUnit: preset.defaultPrice,
      quantity: quantity,
      unit: preset.unit,
      amount: total,
      description: preset.unit === 'm²' ? `${preset.defaultPrice}€/${preset.unit} x ${quantity}${preset.unit}` : ''
    }]);
  };

  const addCustomWork = () => {
    if (!customWork.label || !customWork.priceUnit || !customWork.quantity) {
      toast.error('Remplissez le libellé, le prix unitaire et la quantité');
      return;
    }
    const total = parseFloat(customWork.priceUnit) * parseFloat(customWork.quantity);
    setWorkEstimates([...workEstimates, {
      label: customWork.label,
      priceUnit: parseFloat(customWork.priceUnit),
      quantity: parseFloat(customWork.quantity),
      unit: customWork.unit,
      amount: total,
      description: customWork.description || `${customWork.priceUnit}€/${customWork.unit} x ${customWork.quantity}`
    }]);
    setCustomWork({ label: '', priceUnit: '', quantity: '', unit: 'forfait', description: '' });
  };

  const updateWorkEstimate = (index, field, value) => {
    const updated = [...workEstimates];
    updated[index][field] = value;
    // Recalculate total
    if (field === 'priceUnit' || field === 'quantity') {
      updated[index].amount = (parseFloat(updated[index].priceUnit) || 0) * (parseFloat(updated[index].quantity) || 0);
      updated[index].description = `${updated[index].priceUnit}€/${updated[index].unit} x ${updated[index].quantity}`;
    }
    setWorkEstimates(updated);
  };

  const removeWork = (index) => {
    setWorkEstimates(workEstimates.filter((_, i) => i !== index));
  };

  // Détecte si le profil est Marchand de Biens (prioritaire : selector explicite)
  const isMarchand = fiscalProfile === 'marchand_biens';

  // Calcul des assurances Marchand
  const calculateInsurances = (totalWorks, purchasePrice) => {
    if (!isMarchand) return { do: 0, rcPro: 0, bank: 0, gfa: 0 };
    
    const doAmount = totalWorks * 0.02; // DO = 2% des travaux
    const rcProAmount = 500; // RC Pro forfait
    const bankAmount = purchasePrice * 0.01; // Frais bancaires 1%
    const gfaAmount = 0; // GFA si VEFA (à activer manuellement)
    
    return { do: doAmount, rcPro: rcProAmount, bank: bankAmount, gfa: gfaAmount };
  };

  // Calcul comparatif Particulier vs Marchand
  const calculateMarchandSavings = (purchasePrice) => {
    const notaryParticulier = purchasePrice * 0.08;
    const notaryMarchand = purchasePrice * 0.025;
    const savingsNotary = notaryParticulier - notaryMarchand;
    
    return {
      notaryParticulier,
      notaryMarchand,
      savingsNotary,
      savingsPercent: ((savingsNotary / purchasePrice) * 100).toFixed(1)
    };
  };

  const calculateTotals = () => {
    if (!selectedProject) return { notaryFees: 0, totalWorks: 0, financialFees: 0, insuranceFees: 0, totalCost: 0, netMargin: 0 };
    
    const purchasePrice = selectedProject.purchase_price;
    const notaryFees = purchasePrice * notaryRate;
    const totalWorks = workEstimates.reduce((sum, w) => sum + w.amount, 0);
    const financialFees = purchasePrice * 0.01;
    
    // Assurances Marchand
    const insurances = calculateInsurances(totalWorks, purchasePrice);
    const insuranceFees = insurances.do + insurances.rcPro;
    
    const totalCost = purchasePrice + notaryFees + totalWorks + financialFees + (isMarchand ? insuranceFees : 0);
    // Use resalePrice state if set, otherwise use DVF market estimation (never client's estimated_resale_price)
    const effectiveResalePrice = resalePrice ? parseFloat(resalePrice) : (marketEstimation?.estimated_price || 0);
    const netMargin = effectiveResalePrice > 0 ? effectiveResalePrice - totalCost : 0;
    
    return { notaryFees, totalWorks, financialFees, insuranceFees, insurances, totalCost, netMargin };
  };

  const handleDeleteProject = async () => {
    if (!selectedProject) return;
    const confirmMsg = `Supprimer définitivement le dossier ${selectedProject.address} ?\n\nCette action est irréversible.`;
    if (!window.confirm(confirmMsg)) return;
    if (!activeToken) { toast.error('Session expirée'); return; }
    try {
      const deleteUrl = isPartnerMode
        ? `${process.env.REACT_APP_BACKEND_URL}/api/partenaire/projects/${selectedProject.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${selectedProject.id}`;
      await axios.delete(deleteUrl, {
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      toast.success('Dossier supprimé');
      setSelectedProject(null);
      fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };


  const handleValidate = async () => {
    if (!selectedScore) {
      toast.error('Sélectionnez un score (Validé, Risqué, Refusé)');
      return;
    }
    
    setCalculating(true);
    try {
      const data = {
        score: selectedScore,
        notes: expertNotes,
        estimated_resale_price: resalePrice ? parseFloat(resalePrice) : null,
        notary_rate: notaryRate,
        work_estimates: workEstimates,
        fiscal_profile: fiscalProfile,
        expert_terrain_adjustment: terrainAdjustment !== '' ? parseFloat(terrainAdjustment) : null,
      };
      
      await expertApi.calculate(selectedProject.id, data);
      toast.success('Dossier validé avec succès !');
      fetchProjects();
    } catch (error) {
      console.error('Error validating project:', error);
      toast.error('Erreur lors de la validation');
    } finally {
      setCalculating(false);
    }
  };

  const generatePdf = async () => {
    if (!selectedProject) return;
    
    const authToken = activeToken || localStorage.getItem('token');
    
    if (!authToken) {
      toast.error('Session expirée, veuillez vous reconnecter');
      return;
    }
    
    try {
      toast.info('Génération du PDF en cours...');
      
      // Si des notes ont été sauvegardées, utiliser le mode "notes" pour le PDF
      const hasCustomNotes = !!(selectedProject.expert_notes || '').trim();
      const useNotesParam = hasCustomNotes ? '&use_notes=1' : '';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expert/projects/${selectedProject.id}/pdf?profile=${encodeURIComponent(fiscalProfile)}${useNotesParam}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }
      
      // Get the PDF as blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_audit_${selectedProject.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const sendDevisToClient = async () => {
    if (!selectedProject) return;
    const hasWorks = workEstimates && workEstimates.length > 0;
    const fichesCount = hasWorks ? workEstimates.filter(w => w.fiche_pdf_url).length : 0;
    let confirmMsg;
    if (hasWorks) {
      confirmMsg = includeFiches && fichesCount > 0
        ? `Envoyer au client le rapport d'audit + le devis travaux avec ${fichesCount} fiche(s) PDF produit jointe(s) ?`
        : "Envoyer au client le rapport d'audit + le devis travaux par email ?";
    } else {
      confirmMsg = "Aucun poste de travaux n'a été ajouté.\n\nEnvoyer au client uniquement le rapport d'audit complet (PDF) par email ?";
    }
    if (!window.confirm(confirmMsg)) return;
    const authToken = activeToken || localStorage.getItem('token');
    if (!authToken) { toast.error('Session expirée'); return; }
    setSendingDevis(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/expert/projects/${selectedProject.id}/send-to-client?include_fiches=${includeFiches}`;
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${authToken}` } });
      const { client_email, fiches_attached } = res.data || {};
      const label = hasWorks ? 'Audit + devis envoyés' : 'Rapport audit envoyé';
      toast.success(`${label} à ${client_email}${fiches_attached ? ` (+${fiches_attached} fiche${fiches_attached > 1 ? 's' : ''} PDF)` : ''}`);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi email');
    } finally {
      setSendingDevis(false);
    }
  };

  const fetchRegulatoryCheck = async () => {
    if (!selectedProject) return;
    const authToken = activeToken || localStorage.getItem('token');
    if (!authToken) { toast.error('Session expirée'); return; }
    setRegLoading(true);
    try {
      const profile = fiscalProfile;
      const pc = selectedProject.postal_code || '';
      const addr = selectedProject.address || '';
      const city = selectedProject.city || '';
      const params = new URLSearchParams({
        address: addr, postal_code: pc, city,
        year_built: yearBuilt || '',
        surface: selectedProject.surface || 0,
        dpe_class: dpeClass || '',
        profile,
      });
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expert/regulatory-check?${params.toString()}`,
        { headers: { Authorization: `Bearer ${authToken}` } });
      setRegCheck(res.data);
      if (res.data.dpe_from_ademe && res.data.dpe_from_ademe.classe_energie && !dpeClass) {
        setDpeClass(res.data.dpe_from_ademe.classe_energie);
        toast.success(`DPE trouvé dans la base ADEME : ${res.data.dpe_from_ademe.classe_energie}`);
      } else {
        toast.success(`${res.data.summary.total} alerte(s) détectée(s)`);
      }
    } catch (e) {
      toast.error('Erreur check réglementaire');
    } finally {
      setRegLoading(false);
    }
  };

  const fetchScenarios = async () => {
    if (!selectedProject) return;
    const authToken = activeToken || localStorage.getItem('token');
    if (!authToken) { toast.error('Session expirée'); return; }
    setScenariosLoading(true);
    try {
      const profile = fiscalProfile;
      const totalWorks = workEstimates.reduce((s, w) => s + (w.amount || 0), 0);
      const params = new URLSearchParams({
        purchase_price: selectedProject.purchase_price,
        works_cost: totalWorks,
        surface: selectedProject.surface || 0,
        rental_price_m2: rentalPriceM2 || 0,
        resale_price: resalePrice || 0,
        profile,
        mdb_acquired_with_tva: mdbAcquiredWithTva,
        loan_rate: loanRate,
        loan_duration_years: loanDuration,
        loan_ltv: loanLtv,
        adjustments: JSON.stringify(expertAdjustments),
      });
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expert/financial-scenarios?${params.toString()}`,
        { headers: { Authorization: `Bearer ${authToken}` } });
      setScenarios(res.data);
      toast.success('Scénarios calculés');
    } catch (e) {
      toast.error('Erreur calcul scénarios');
    } finally {
      setScenariosLoading(false);
    }
  };

  const addAdjustment = () => {
    if (!newAdjustment.label || !newAdjustment.amount) {
      toast.error('Label et montant requis');
      return;
    }
    const adj = {
      label: newAdjustment.label,
      amount: parseFloat(newAdjustment.amount),
      type: newAdjustment.type,
    };
    setExpertAdjustments([...expertAdjustments, adj]);
    setNewAdjustment({ label: '', amount: '', type: 'valorisation' });
    toast.success('Ajustement ajouté');
  };

  const removeAdjustment = (index) => {
    const updated = expertAdjustments.filter((_, i) => i !== index);
    setExpertAdjustments(updated);
    toast.success('Ajustement supprimé');
  };

  const saveAdjustments = async () => {
    if (!selectedProject) return;
    setSavingAdjustments(true);
    try {
      const authToken = activeToken || localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${selectedProject.id}`,
        { expert_adjustments: expertAdjustments },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success('Ajustements sauvegardés ✓');
      // Recalculer les scénarios avec les nouveaux ajustements
      await fetchScenarios();
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingAdjustments(false);
    }
  };

  const fetchEnrichment = async () => {
    if (!selectedProject) return;
    const authToken = activeToken || localStorage.getItem('token');
    if (!authToken) { toast.error('Session expirée'); return; }
    setEnrichmentLoading(true);
    try {
      const params = new URLSearchParams({
        project_id: selectedProject.id,
        income_tier: incomeTier || '',
        household_size: householdSize,
        dpe_before: dpeClass || '',
        dpe_after: dpeAfter || '',
        is_copro: isCopro,
        has_elec_work: hasElecWork,
      });
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expert/enrichment-full?${params.toString()}`,
        { headers: { Authorization: `Bearer ${authToken}` } });
      setEnrichment(res.data);
      toast.success('Enrichissement calculé');
    } catch (e) {
      toast.error('Erreur enrichissement');
    } finally {
      setEnrichmentLoading(false);
    }
  };

  const downloadAuditReport = async () => {
    if (!selectedProject) return;
    const authToken = activeToken || localStorage.getItem('token');
    if (!authToken) { toast.error('Session expirée'); return; }
    setDownloadingAuditPdf(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expert/projects/${selectedProject.id}/audit-report-pdf?profile=${encodeURIComponent(fiscalProfile)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('fail');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_complet_${selectedProject.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Rapport audit complet téléchargé');
    } catch (e) {
      toast.error('Erreur téléchargement rapport');
    } finally {
      setDownloadingAuditPdf(false);
    }
  };

  // Charge le rapport complet (toutes sections PDF) dans la zone de notes pour édition
  const [loadingFullReport, setLoadingFullReport] = useState(false);
  const loadFullReportInNotes = async () => {
    if (!selectedProject) return;
    const authToken = activeToken || localStorage.getItem('token');
    if (!authToken) { toast.error('Session expirée'); return; }
    setLoadingFullReport(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/expert/projects/${selectedProject.id}/audit-report-text?profile=${encodeURIComponent(fiscalProfile)}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!res.ok) throw new Error('fail');
      const data = await res.json();
      setExpertNotes(data.text || '');
      toast.success('Rapport complet chargé — vous pouvez maintenant modifier chaque section');
    } catch (e) {
      toast.error('Erreur chargement rapport texte');
    } finally {
      setLoadingFullReport(false);
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  };

  const { notaryFees, totalWorks, financialFees, insuranceFees, insurances, totalCost, netMargin } = calculateTotals();
  const marginPercent = resalePrice && totalCost > 0 ? ((netMargin / totalCost) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-800 dark">
      {/* Header */}
      <header className="bg-navy border-b border-white/10">
        <div className="px-3 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 md:gap-4 min-w-0">
              <Button
                data-testid="back-to-dashboard-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-white/70 hover:text-white hover:bg-white/10 shrink-0"
              >
                <ArrowLeft className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Retour</span>
              </Button>
              <div className="h-6 w-px bg-white/20 hidden md:block" />
              <h1 className="font-heading text-base md:text-xl font-bold text-white truncate">
                <span className="hidden sm:inline">DASHBOARD </span>EXPERT
                {isPartnerMode && partnerSelf && (
                  <span className="ml-1 md:ml-2 text-xs md:text-sm font-normal text-gold/80 bg-gold/10 px-1.5 md:px-2 py-0.5 rounded">
                    {partnerSelf.company_name || partnerSelf.full_name || 'Partenaire'}
                  </span>
                )}
                {!isPartnerMode && partnerFilter && (
                  <span className="ml-2 text-sm font-normal text-gold/80 bg-gold/10 px-2 py-0.5 rounded">
                    {partnerFilter}
                  </span>
                )}
              </h1>
            </div>
            <div className="flex items-center gap-1 md:gap-3 shrink-0">
              {/* Bouton retour espace partenaire si mode partenaire */}
              {isPartnerMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/partenaire/dashboard')}
                  className="border-gold text-gold hover:bg-gold hover:text-black text-xs md:text-sm"
                >
                  <ArrowLeft className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Espace Partenaire</span>
                </Button>
              )}
              {/* Administration button - for admin and expert users only */}
              {!isPartnerMode && (user?.role === 'admin' || user?.role === 'expert') && (
                <Button
                  data-testid="admin-dashboard-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin/dashboard')}
                  className="border-gold text-gold hover:bg-gold hover:text-black hidden md:flex"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Administration
                </Button>
              )}
              <Badge className="bg-gold text-black text-xs hidden sm:flex">
                <FolderOpen className="w-3 h-3 mr-1" />
                {projects.length}
              </Badge>
              <Button
                data-testid="refresh-btn"
                variant="ghost"
                size="icon"
                onClick={fetchProjects}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                data-testid="logout-expert-btn"
                variant="ghost"
                size="icon"
                onClick={logout}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)] overflow-hidden">
        {/* Sidebar - Projects List */}
        <aside className={`${mobileShowDetail ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-72 bg-navy-700 border-r border-white/10 overflow-y-auto flex-shrink-0`}>
          <div className="p-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-white/50 mb-3">Dossiers</h2>

            {/* Filtre partenaire (admin uniquement) */}
            {!isPartnerMode && partnersList.length > 0 && (
              <div className="mb-3">
                <select
                  value={activePartnerFilter}
                  onChange={(e) => {
                    const val = e.target.value;
                    setActivePartnerFilter(val);
                    setSelectedProject(null);
                    fetchProjects(val);
                  }}
                  className="w-full text-xs bg-white/10 text-white border border-white/20 rounded-md px-2 py-1.5 focus:outline-none focus:border-gold/50"
                >
                  <option value="">Tous les partenaires</option>
                  <option value="__no_partner__" style={{background:'#1e3a5f'}}>— Sans partenaire —</option>
                  {partnersList.map(p => (
                    <option key={p.id} value={p.subdomain} style={{background:'#1e3a5f'}}>
                      {p.company_name || p.full_name || p.subdomain}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-2">
              {projects.map((project) => (
                <button
                  key={project.id}
                  data-testid={`project-item-${project.id}`}
                  onClick={() => { 
                    setSelectedProject(project); 
                    setMobileShowDetail(true); 
                    setTimeout(refreshUnread, 1500);
                    // Scroll en haut uniquement lors du clic explicite de l'utilisateur
                    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
                  }}
                  className={`w-full p-3 rounded-lg text-left transition-all relative ${
                    selectedProject?.id === project.id
                      ? 'bg-gold/20 border border-gold/50'
                      : 'bg-white/5 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <UnreadDot count={unreadByProject[project.id]} className="absolute top-2 right-2" />
                  <div className="flex items-start gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      project.expert_score === 'green' ? 'bg-emerald-500' :
                      project.expert_score === 'orange' ? 'bg-amber-500' :
                      project.expert_score === 'red' ? 'bg-red-500' : 'bg-gray-500'
                    }`} />
                    <div className="min-w-0 w-full">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-sm font-medium text-white truncate">
                          #{project.id.slice(0, 6).toUpperCase()}
                        </p>
                        {!isPartnerMode && project.partner_subdomain && (
                          <span className="text-[9px] bg-gold/20 text-gold border border-gold/30 px-1 py-0.5 rounded shrink-0 max-w-[70px] truncate">
                            {project.partner_subdomain}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-white/60 truncate">{project.address}</p>
                      <p className="text-xs text-white/40 mt-1">{project.surface}m² • {formatPrice(project.purchase_price)}</p>
                      {project.created_at && (
                        <p className="text-[10px] text-white/30 mt-0.5">
                          Reçu le {new Date(project.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        {selectedProject ? (
          <main ref={mainScrollRef} className={`${mobileShowDetail ? 'flex' : 'hidden md:flex'} flex-1 flex-col overflow-y-auto p-4 md:p-6`}>
            {/* Mobile back button */}
            <button
              className="md:hidden flex items-center gap-2 text-white/70 hover:text-white mb-4 -ml-1"
              onClick={() => setMobileShowDetail(false)}
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Retour aux dossiers</span>
            </button>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Project Info & Videos */}
              <div className="lg:col-span-1 space-y-6">
                {/* Project Header */}
                <Card className="bg-navy-700 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-gold" />
                      Dossier #{selectedProject.id.slice(0, 6).toUpperCase()}
                      {selectedProject.payment_status !== 'paid' ? (
                        <button
                          onClick={async () => {
                            try {
                              const axios = require('axios');
                              await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/expert/projects/${selectedProject.id}/payment-status`, { payment_status: 'paid' });
                              setSelectedProject(prev => ({ ...prev, payment_status: 'paid' }));
                              setProjects(prev => prev.map(p => p.id === selectedProject.id ? { ...p, payment_status: 'paid' } : p));
                              toast.success('Dossier marqué comme payé');
                            } catch(e) { toast.error('Erreur'); }
                          }}
                          className="ml-2 bg-red-100 text-red-700 text-[10px] py-0.5 px-2 rounded-full border border-red-300 hover:bg-emerald-100 hover:text-emerald-700 hover:border-emerald-300 transition-colors cursor-pointer"
                          title="Cliquer pour marquer comme payé"
                        >
                          ⚠ NON PAYÉ — cliquer pour corriger
                        </button>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px] py-0 px-1.5 h-4 ml-2">✓ PAYÉ</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-white/60">Adresse</p>
                      <p className="text-white font-medium">
                        {selectedProject.address}
                        {selectedProject.address_precision && (
                          <span className="italic text-amber-300 ml-2">— {selectedProject.address_precision}</span>
                        )}
                      </p>
                    </div>

                    {/* Client info */}
                    {(selectedProject.client_name || selectedProject.client_email) && (
                      <div className="bg-gold/10 border border-gold/20 rounded-lg px-3 py-2.5 flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Client</p>
                          {selectedProject.client_name && (
                            <p className="text-white font-semibold text-sm">{selectedProject.client_name}</p>
                          )}
                          {selectedProject.client_email && (
                            <a
                              href={`mailto:${selectedProject.client_email}`}
                              className="text-gold text-xs hover:underline flex items-center gap-1 mt-0.5"
                            >
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              {selectedProject.client_email}
                            </a>
                          )}
                          {selectedProject.client_phone && (
                            <p className="text-white/60 text-xs mt-0.5">{selectedProject.client_phone}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Mini-carte de localisation du bien */}
                    <ProjectMiniMap
                      latitude={selectedProject.latitude}
                      longitude={selectedProject.longitude}
                      address={selectedProject.address}
                      address_precision={selectedProject.address_precision}
                      height={200}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-white/60">Surface habitable</p>
                        <p className="text-white font-medium">{selectedProject.surface} m²</p>
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Prix d'achat</p>
                        <p className="text-gold font-bold">{formatPrice(selectedProject.purchase_price)}</p>
                      </div>
                      {selectedProject.land_surface > 0 && (
                        <div className="col-span-2">
                          <p className="text-sm text-white/60">🌳 Surface terrain</p>
                          <p className="text-white font-medium">{selectedProject.land_surface} m²</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-white/60">Profil (choix client)</p>
                      <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 mt-1">
                        {(() => {
                          const raw = selectedProject.fiscal_profile || 'particulier';
                          const fp = raw === 'marchand' ? 'marchand_biens' : raw;
                          return ({
                            particulier: '👤 Particulier',
                            sci_ir: "🏛️ SCI à l'IR",
                            sci_is: "🏢 SCI à l'IS",
                            marchand_biens: '💼 Marchand de Biens',
                          })[fp] || '👤 Particulier';
                        })()}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Médias client — photos, vidéos, documents */}
                <Card className="bg-navy-700 border-white/10" data-testid="expert-client-media-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Video className="w-5 h-5 text-gold" />
                      Médias du client
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedProject.videos && selectedProject.videos.length > 0 ? (
                      (() => {
                        const medias = selectedProject.videos.map(m => ({ ...m, _kind: _getMediaKind(m) }));
                        const images = medias.filter(m => m._kind === 'image');
                        const videos = medias.filter(m => m._kind === 'video' || m._kind === 'audio');
                        const docs = medias.filter(m => m._kind === 'document');

                        return (
                          <div className="space-y-4">
                            {/* IMAGES */}
                            {images.length > 0 && (
                              <div>
                                <div className="text-xs uppercase tracking-wide text-white/60 mb-2 flex items-center gap-1">
                                  <ImageIcon className="w-3.5 h-3.5" /> Photos ({images.length})
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  {images.map((img) => {
                                    const url = projectsApi.getVideoUrl(selectedProject.id, img.id, token);
                                    return (
                                      <a
                                        key={img.id}
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`view-image-${img.id}`}
                                        className="group relative block aspect-square rounded-lg overflow-hidden bg-black/40 border border-white/10 hover:border-gold transition-all"
                                        title={_getMediaLabel(img)}
                                      >
                                        <img
                                          src={url}
                                          alt={_getMediaLabel(img)}
                                          className="w-full h-full object-cover"
                                          loading="lazy"
                                        />
                                        <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[10px] px-1.5 py-1 truncate">
                                          {_getMediaLabel(img)}
                                        </div>
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* VIDÉOS + AUDIO */}
                            {videos.length > 0 && (
                              <div>
                                <div className="text-xs uppercase tracking-wide text-white/60 mb-2 flex items-center gap-1">
                                  <Video className="w-3.5 h-3.5" /> Vidéos & audio ({videos.length})
                                </div>
                                <div className="space-y-2">
                                  {videos.map((video) => (
                                    <button
                                      key={video.id}
                                      data-testid={`play-video-${video.id}`}
                                      onClick={() => setActiveVideo(video)}
                                      className={`w-full p-3 rounded-lg flex items-center gap-3 transition-all ${
                                        activeVideo?.id === video.id
                                          ? 'bg-gold/20 border border-gold'
                                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                                      }`}
                                    >
                                      <Play className="w-4 h-4 text-gold flex-shrink-0" />
                                      <span className="text-white text-sm text-left flex-1 truncate">{_getMediaLabel(video)}</span>
                                    </button>
                                  ))}
                                </div>
                                {activeVideo && (
                                  <div className="mt-3 rounded-lg overflow-hidden bg-black aspect-video">
                                    {(activeVideo._kind === 'audio' || (activeVideo.content_type || '').startsWith('audio/')) ? (
                                      <audio
                                        src={projectsApi.getVideoUrl(selectedProject.id, activeVideo.id, token)}
                                        controls
                                        className="w-full mt-8"
                                      />
                                    ) : (
                                      <video
                                        src={projectsApi.getVideoUrl(selectedProject.id, activeVideo.id, token)}
                                        controls
                                        className="w-full h-full"
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* DOCUMENTS */}
                            {docs.length > 0 && (
                              <div>
                                <div className="text-xs uppercase tracking-wide text-white/60 mb-2 flex items-center gap-1">
                                  <FileIcon className="w-3.5 h-3.5" /> Documents ({docs.length})
                                </div>
                                <div className="space-y-2">
                                  {docs.map((doc) => {
                                    const url = projectsApi.getVideoUrl(selectedProject.id, doc.id, token);
                                    return (
                                      <a
                                        key={doc.id}
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        download={doc.original_filename || undefined}
                                        data-testid={`download-doc-${doc.id}`}
                                        className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-transparent hover:border-gold transition-all"
                                      >
                                        <FileText className="w-4 h-4 text-gold flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <div className="text-white text-sm truncate">{_getMediaLabel(doc)}</div>
                                          {doc.original_filename && (
                                            <div className="text-white/50 text-xs truncate">{doc.original_filename}</div>
                                          )}
                                        </div>
                                        <Download className="w-4 h-4 text-white/60 flex-shrink-0" />
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <p className="text-white/50 text-sm text-center py-4">Aucun média fourni par le client</p>
                    )}
                  </CardContent>
                </Card>

                {/* ============ COMMUNICATION CLIENT (messagerie + documents par dossier) ============ */}
                <Card className="bg-navy-700 border-white/10" data-testid="expert-communication-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-gold" />
                      Communication & Documents
                      <span className="text-xs font-normal text-white/50">(spécifique à CE dossier)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ProjectCommunication projectId={selectedProject.id} token={token} compact />
                  </CardContent>
                </Card>
              </div>

              {/* Calculator */}
              <div className="lg:col-span-2 space-y-6">
                {/* ============ PROFIL FISCAL (SÉLECTEUR EXPLICITE) ============ */}
                {(() => {
                  // Profil choisi par le client à la commande (verrouillé visuellement)
                  const rawClient = selectedProject?.fiscal_profile || 'particulier';
                  const clientFiscal = rawClient === 'marchand' ? 'marchand_biens' : rawClient;
                  const PROFILES_META = {
                    particulier:    { label: 'Particulier',         emoji: '👤', color: 'blue' },
                    sci_ir:         { label: "SCI à l'IR",          emoji: '🏛️', color: 'cyan' },
                    sci_is:         { label: "SCI à l'IS",          emoji: '🏢', color: 'indigo' },
                    marchand_biens: { label: 'Marchand de Biens',   emoji: '💼', color: 'amber' },
                  };
                  const clientMeta = PROFILES_META[clientFiscal] || PROFILES_META.particulier;
                  const isModified = fiscalProfile !== clientFiscal;
                  return (
                <Card className="bg-navy-700 border-white/10" data-testid="fiscal-profile-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-gold" />
                      Profil Fiscal de l'Opération
                      <span className="text-xs font-normal text-white/50">(détermine tous les calculs de plus-value)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">

                    {/* === ENCADRÉ "CHOIX DU CLIENT" — toujours visible === */}
                    <div
                      className="p-3 rounded-lg border-2 border-emerald-500/40 bg-emerald-500/10 flex items-center gap-3"
                      data-testid="client-fiscal-profile-readonly"
                    >
                      <div className="text-3xl">{clientMeta.emoji}</div>
                      <div className="flex-1">
                        <div className="text-emerald-200 text-xs uppercase tracking-wider font-bold">
                          📌 Choix initial du client (à la commande)
                        </div>
                        <div className="text-white text-lg font-bold mt-0.5">
                          {clientMeta.label}
                        </div>
                      </div>
                      {isModified && (
                        <button
                          onClick={() => setFiscalProfile(clientFiscal)}
                          className="px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold whitespace-nowrap"
                          data-testid="reset-fiscal-profile-btn"
                          title="Restaurer le choix du client"
                        >
                          ↺ Restaurer
                        </button>
                      )}
                    </div>

                    {/* === BANNIÈRE "MODIFIÉ" si différent du client === */}
                    {isModified && (
                      <div
                        className="p-2.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-sm flex items-center gap-2"
                        data-testid="fiscal-profile-modified-banner"
                      >
                        <span className="text-amber-300 text-base">⚠️</span>
                        <span className="text-amber-100">
                          <b>Profil modifié par l'expert</b> — Calculs basés sur <b>{(PROFILES_META[fiscalProfile] || {}).label}</b> au lieu du choix client (<b>{clientMeta.label}</b>).
                        </span>
                      </div>
                    )}

                    <div className="text-xs text-white/60 mt-1">
                      Sélecteur de simulation (modification possible avec confirmation) :
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[
                        { id: 'particulier',    label: 'Particulier',       emoji: '👤' },
                        { id: 'sci_ir',         label: "SCI à l'IR",        emoji: '🏛️' },
                        { id: 'sci_is',         label: "SCI à l'IS",        emoji: '🏢' },
                        { id: 'marchand_biens', label: 'Marchand de Biens', emoji: '💼' },
                      ].map((p) => {
                        const isClient = p.id === clientFiscal;
                        const isSelected = fiscalProfile === p.id;
                        return (
                          <button
                            key={p.id}
                            data-testid={`fiscal-profile-${p.id}`}
                            onClick={() => {
                              if (p.id === fiscalProfile) return;
                              if (p.id !== clientFiscal) {
                                const ok = window.confirm(
                                  `⚠️ Vous êtes sur le point de modifier le profil fiscal du dossier.\n\n` +
                                  `Choix initial du client : ${clientMeta.label}\n` +
                                  `Nouveau profil : ${(PROFILES_META[p.id] || {}).label}\n\n` +
                                  `Cette modification écrasera le profil enregistré lors de la validation. Continuer ?`
                                );
                                if (!ok) return;
                              }
                              setFiscalProfile(p.id);
                            }}
                            className={`p-3 rounded-lg border-2 text-left transition-all relative ${
                              isSelected
                                ? 'border-gold bg-gold/15 shadow-lg shadow-gold/10'
                                : 'border-white/10 bg-white/5 hover:border-white/30'
                            }`}
                          >
                            {isClient && (
                              <span
                                className="absolute -top-1.5 -right-1.5 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow"
                                data-testid={`client-badge-${p.id}`}
                              >
                                CLIENT
                              </span>
                            )}
                            <div className="text-2xl mb-1">{p.emoji}</div>
                            <div className="text-white text-sm font-semibold">{p.label}</div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Fiche explicative par profil */}
                    <div className="mt-2 p-3 rounded-lg bg-white/5 border border-white/10 text-sm" data-testid="fiscal-profile-info">
                      {fiscalProfile === 'particulier' && (
                        <div className="space-y-1 text-white/80">
                          <div className="text-blue-300 font-semibold">👤 Particulier — Personne Physique</div>
                          <div>• Frais notaire : <b>7-8%</b> de l'ancien</div>
                          <div>• Plus-value à la revente : <b>19% IR + 17,2% PS = 36,2%</b></div>
                          <div>• Abattement : 0% avant 6 ans, exonération totale après 22 ans (30 ans pour PS)</div>
                          <div>• <b>Résidence principale</b> : plus-value 100% exonérée</div>
                        </div>
                      )}
                      {fiscalProfile === 'sci_ir' && (
                        <div className="space-y-1 text-white/80">
                          <div className="text-cyan-300 font-semibold">🏛️ SCI à l'Impôt sur le Revenu (transparence fiscale)</div>
                          <div>• Frais notaire : <b>7-8%</b></div>
                          <div>• Plus-value : <b>même régime que le Particulier</b> (36,2% + abattements pour durée)</div>
                          <div>• Les revenus locatifs sont imposés au <b>nom des associés</b> (barème IR + PS)</div>
                          <div>• <b>Pas d'amortissement</b> comptable possible</div>
                          <div className="text-cyan-200 italic">Recommandé pour : transmission familiale, détention patrimoniale long terme.</div>
                        </div>
                      )}
                      {fiscalProfile === 'sci_is' && (
                        <div className="space-y-1 text-white/80">
                          <div className="text-indigo-300 font-semibold">🏢 SCI à l'Impôt sur les Sociétés</div>
                          <div>• Frais notaire : <b>7-8%</b></div>
                          <div>• Plus-value : <b>IS 15%</b> jusqu'à 42 500 € puis <b>25%</b> (plus-value = prix vente − valeur nette comptable)</div>
                          <div>• <b>Amortissement</b> du bâti (30-40 ans) → baisse l'impôt annuel mais augmente la plus-value à la revente</div>
                          <div>• Double imposition si distribution de dividendes (Flat Tax 30%)</div>
                          <div className="text-indigo-200 italic">Recommandé pour : location meublée longue durée, réinvestissement dans la société.</div>
                        </div>
                      )}
                      {fiscalProfile === 'marchand_biens' && (
                        <div className="space-y-1 text-white/80">
                          <div className="text-amber-300 font-semibold">💼 Marchand de Biens (activité commerciale)</div>
                          <div>• Frais notaire : <b>2,5%</b> (droits réduits + engagement de revendre sous 5 ans)</div>
                          <div>• <b>TVA sur marge</b> (si acquis d'un assujetti) ou <b>TVA sur prix total</b> (sinon)</div>
                          <div>• IS sur résultat net : 15% / 25%</div>
                          <div>• Obligations : RC Pro, Dommage-Ouvrage, garantie financière</div>
                          <div className="text-amber-200 italic">Recommandé pour : achat-revente rapide (flip), rénovation lourde.</div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                  );
                })()}

                {/* === AUDIT FISCAL OPTIMISÉ — données complétables si client envoie par email === */}
                <Card className="bg-amber-500/10 border-amber-400/40" data-testid="audit-fiscal-optim-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <span className="text-2xl">🧾</span>
                      Audit fiscal optimisé
                      <span className="text-xs font-normal text-white/50">(complétable a posteriori)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-amber-200/90">
                      Ces 4 informations permettent de calculer les abattements pour durée de détention (jusqu'à -100% d'impôt PV !) et l'exonération résidence principale. Si le client les envoie par email, complétez ici puis régénérez le PDF — aucun nouveau paiement requis.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white/80 text-xs">Année d'acquisition</Label>
                        <input
                          type="number"
                          min="1900" max={new Date().getFullYear()}
                          value={purchaseYear}
                          onChange={(e) => setPurchaseYear(e.target.value)}
                          placeholder="ex: 2015"
                          data-testid="expert-purchase-year"
                          className="w-full mt-1 px-3 py-2 bg-navy-800 border border-white/20 rounded text-white text-sm focus:border-amber-400 focus:outline-none"
                        />
                      </div>
                      <div>
                        <Label className="text-white/80 text-xs">Prix d'achat historique (€)</Label>
                        <input
                          type="number"
                          min="0"
                          value={historicalPrice}
                          onChange={(e) => setHistoricalPrice(e.target.value)}
                          placeholder="Si différent du prix actuel"
                          data-testid="expert-historical-price"
                          className="w-full mt-1 px-3 py-2 bg-navy-800 border border-white/20 rounded text-white text-sm focus:border-amber-400 focus:outline-none"
                        />
                      </div>
                      <div>
                        <Label className="text-white/80 text-xs">Année de construction</Label>
                        <input
                          type="number"
                          min="1700" max={new Date().getFullYear()}
                          value={constructionYear}
                          onChange={(e) => setConstructionYear(e.target.value)}
                          placeholder="ex: 1985"
                          data-testid="expert-construction-year"
                          className="w-full mt-1 px-3 py-2 bg-navy-800 border border-white/20 rounded text-white text-sm focus:border-amber-400 focus:outline-none"
                        />
                      </div>
                      <div>
                        <Label className="text-white/80 text-xs">Résidence principale ?</Label>
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            checked={isResidencePrincipale}
                            onChange={(e) => setIsResidencePrincipale(e.target.checked)}
                            data-testid="expert-rp-checkbox"
                            className="w-5 h-5 accent-amber-500"
                          />
                          <span className="text-sm text-white/80">Oui (exonération PV totale)</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={saveFiscalOptim}
                      disabled={savingFiscal}
                      className="bg-amber-500 hover:bg-amber-600 text-white w-full"
                      data-testid="save-fiscal-optim-btn"
                    >
                      {savingFiscal ? 'Enregistrement…' : '💾 Enregistrer & appliquer aux calculs'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Notary Rate */}
                <Card className="bg-navy-700 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-gold" />
                      Frais de Notaire / Type d'Acquisition
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RadioGroup
                      value={notaryRate.toString()}
                      onValueChange={(v) => setNotaryRate(parseFloat(v))}
                      className="flex flex-wrap gap-4"
                    >
                      {NOTARY_RATES.map((rate) => (
                        <div key={rate.id} className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value={rate.rate.toString()} 
                            id={rate.id}
                            data-testid={`notary-rate-${rate.id}`}
                            className="border-white/30 text-gold"
                          />
                          <Label htmlFor={rate.id} className="text-white cursor-pointer">{rate.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <p className="text-xs text-white/50 mt-2 italic">
                      Enchères (20%) = droits d'enregistrement + émoluments + frais poursuivant. Marchand (2,5%) = engagement de revente sous 5 ans.
                    </p>
                  </CardContent>
                </Card>

                {/* ============ PHASE 1 — ALERTES RÉGLEMENTAIRES ============ */}
                <Card className="bg-navy-700 border-white/10" data-testid="regulatory-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      🚨 Alertes Réglementaires
                      <span className="text-xs font-normal text-white/50">(DPE · PPRI · sismique · obligations)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-white/80 text-xs mb-1 block">Classe DPE (A → G)</Label>
                        <div className="flex gap-2">
                          <select
                            value={dpeClass}
                            onChange={(e) => setDpeClass(e.target.value)}
                            className="flex-1 h-9 bg-white/10 border border-white/20 text-white rounded px-2 text-sm"
                            data-testid="dpe-class-select"
                          >
                            <option value="">— Sélectionner —</option>
                            {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          <button
                            type="button"
                            onClick={() => setShowDpeHint(!showDpeHint)}
                            className="px-2 h-9 bg-amber-500/20 border border-amber-400/30 rounded hover:bg-amber-500/30 transition-colors text-white text-xs whitespace-nowrap"
                            title="Astuce : Estimer le DPE"
                          >
                            💡
                          </button>
                        </div>
                        
                        {showDpeHint && (
                          <div className="absolute z-50 mt-2 p-4 bg-navy-600 border-2 border-amber-400 rounded-lg shadow-2xl text-sm w-96">
                            <div className="font-semibold text-amber-300 mb-3 flex items-center justify-between">
                              <span>💡 Estimer le DPE</span>
                              <button onClick={() => setShowDpeHint(false)} className="text-white/50 hover:text-white">✕</button>
                            </div>
                            
                            <div className="space-y-3 text-white/90">
                              <div>
                                <div className="font-medium text-amber-200 mb-1">Selon l'année :</div>
                                <ul className="ml-4 space-y-1 text-xs text-white/80">
                                  <li>• Avant 1975 → <strong className="text-red-300">E, F ou G</strong></li>
                                  <li>• 1975-2000 → <strong className="text-orange-300">D ou E</strong></li>
                                  <li>• 2001-2012 → <strong className="text-yellow-300">C ou D</strong></li>
                                  <li>• Après 2012 → <strong className="text-green-300">B ou C</strong></li>
                                  <li>• Après 2021 → <strong className="text-emerald-300">A ou B</strong></li>
                                </ul>
                              </div>
                              
                              <div>
                                <div className="font-medium text-amber-200 mb-1">Selon l'isolation :</div>
                                <ul className="ml-4 space-y-1 text-xs text-white/80">
                                  <li>• Pas d'isolation → <strong className="text-red-300">F ou G</strong></li>
                                  <li>• Isolation partielle → <strong className="text-orange-300">D ou E</strong></li>
                                  <li>• Complète + chauffage → <strong className="text-green-300">B ou C</strong></li>
                                </ul>
                              </div>
                              
                              <div className="text-xs italic text-amber-200/80 bg-amber-900/20 p-2 rounded">
                                ⚠️ ESTIMATION uniquement ! Précisez dans vos notes.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-white/80 text-xs mb-1 block">Année construction</Label>
                        <Input
                          type="number"
                          value={yearBuilt}
                          onChange={(e) => setYearBuilt(e.target.value)}
                          placeholder="Ex: 1975"
                          className="h-9 bg-white/10 border-white/20 text-white"
                          data-testid="year-built-input"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={fetchRegulatoryCheck}
                          disabled={regLoading}
                          className="w-full bg-amber-600 hover:bg-amber-700 text-white h-9 text-sm"
                          data-testid="fetch-regulatory-btn"
                        >
                          {regLoading ? 'Vérification...' : '🔍 Vérifier alertes'}
                        </Button>
                      </div>
                    </div>
                    {regCheck && regCheck.dpe_from_ademe && (
                      <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded p-2">
                        ✅ DPE retrouvé dans la base ADEME : <b>{regCheck.dpe_from_ademe.classe_energie}</b>
                        {regCheck.dpe_from_ademe.date && ` (émis le ${regCheck.dpe_from_ademe.date.substring(0, 10)})`}
                      </div>
                    )}
                    {regCheck && regCheck.alerts && regCheck.alerts.length > 0 && (
                      <div className="space-y-2 mt-2">
                        <div className="flex gap-3 text-xs">
                          <span className="text-red-400 font-semibold">🔴 {regCheck.summary.critical} critiques</span>
                          <span className="text-amber-400 font-semibold">🟠 {regCheck.summary.warning} à surveiller</span>
                          <span className="text-emerald-400 font-semibold">🟢 {regCheck.summary.info} infos</span>
                        </div>
                        {regCheck.alerts.map((a, i) => (
                          <div
                            key={i}
                            className={`rounded-lg p-3 border ${
                              a.level === 'critical' ? 'bg-red-500/10 border-red-500/40' :
                              a.level === 'warning' ? 'bg-amber-500/10 border-amber-500/40' :
                              'bg-emerald-500/10 border-emerald-500/40'
                            }`}
                            data-testid={`reg-alert-${i}`}
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-lg">{a.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-white text-sm">{a.title}</div>
                                <div className="text-white/70 text-xs mt-1">{a.text}</div>
                                {a.action && <div className="text-white/90 text-xs mt-1"><b>Action :</b> {a.action}</div>}
                                {a.financial_impact && a.financial_impact !== '—' && (
                                  <div className="text-gold text-xs mt-1"><b>Impact financier :</b> {a.financial_impact}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {regCheck && regCheck.alerts && regCheck.alerts.length === 0 && (
                      <p className="text-emerald-300 text-sm italic">Aucune alerte détectée. Profil réglementaire sain.</p>
                    )}
                  </CardContent>
                </Card>

                {/* ============ PHASE 2 — SIMULATION FINANCIÈRE ============ */}
                <Card className="bg-navy-700 border-white/10" data-testid="scenarios-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      💰 Simulation Financière
                      <span className="text-xs font-normal text-white/50">
                        ({fiscalProfile === 'marchand_biens' ? 'Marchand de Biens' :
                          fiscalProfile === 'sci_is' ? 'SCI à l\'IS' :
                          fiscalProfile === 'sci_ir' ? 'SCI à l\'IR' :
                          'Particulier'})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-white/70 text-[11px] mb-0.5 block">Loyer marché €/m²</Label>
                        <Input type="number" value={rentalPriceM2} onChange={(e) => setRentalPriceM2(e.target.value)} placeholder="15" className="h-9 bg-white/10 border-white/20 text-white text-sm" data-testid="rental-m2-input" />
                      </div>
                      <div>
                        <Label className="text-white/70 text-[11px] mb-0.5 block">Taux crédit %</Label>
                        <Input type="number" step="0.1" value={loanRate} onChange={(e) => setLoanRate(parseFloat(e.target.value) || 0)} className="h-9 bg-white/10 border-white/20 text-white text-sm" data-testid="loan-rate-input" />
                      </div>
                      <div>
                        <Label className="text-white/70 text-[11px] mb-0.5 block">Durée (ans)</Label>
                        <Input type="number" value={loanDuration} onChange={(e) => setLoanDuration(parseInt(e.target.value) || 20)} className="h-9 bg-white/10 border-white/20 text-white text-sm" data-testid="loan-duration-input" />
                      </div>
                      <div>
                        <Label className="text-white/70 text-[11px] mb-0.5 block">LTV % (crédit)</Label>
                        <Input type="number" value={loanLtv} onChange={(e) => setLoanLtv(parseInt(e.target.value) || 80)} className="h-9 bg-white/10 border-white/20 text-white text-sm" data-testid="loan-ltv-input" />
                      </div>
                    </div>
                    {isMarchand && (
                      <label className="flex items-center gap-2 text-sm text-white/90 cursor-pointer">
                        <input type="checkbox" checked={mdbAcquiredWithTva} onChange={(e) => setMdbAcquiredWithTva(e.target.checked)} className="w-4 h-4 accent-amber-500" data-testid="tva-acquired-checkbox" />
                        Bien acquis auprès d'un assujetti TVA (change le régime TVA sur marge)
                      </label>
                    )}
                    <Button onClick={fetchScenarios} disabled={scenariosLoading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fetch-scenarios-btn">
                      {scenariosLoading ? 'Calcul...' : '📊 Calculer les scénarios'}
                    </Button>
                    {scenarios && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                        <div className="bg-gradient-to-br from-emerald-900/40 to-navy-800 rounded-lg p-3 border border-emerald-500/30" data-testid="scenario-revente">
                          <div className="text-emerald-300 text-xs font-bold uppercase tracking-wide mb-2">🔄 Scénario Revente</div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between text-white/80"><span>Prix revente</span><span>{scenarios.scenario_revente.resale_price?.toLocaleString('fr-FR')} €</span></div>
                            <div className="flex justify-between text-white/70"><span>Frais revente</span><span>-{scenarios.scenario_revente.frais_revente?.toLocaleString('fr-FR')} €</span></div>
                            <div className="flex justify-between text-white/70"><span>PV brute</span><span>{scenarios.scenario_revente.plus_value_brute?.toLocaleString('fr-FR')} €</span></div>
                            <div className="text-[11px] text-white/60 italic">{scenarios.scenario_revente.fiscalite_label}</div>
                            <div className="flex justify-between text-red-300"><span>Impôts</span><span>-{scenarios.scenario_revente.impot_total?.toLocaleString('fr-FR')} €</span></div>
                            <div className="border-t border-white/10 my-1" />
                            <div className="flex justify-between text-gold font-bold text-base"><span>Net après impôts</span><span>{scenarios.scenario_revente.net_apres_impots?.toLocaleString('fr-FR')} €</span></div>
                            <div className="flex justify-between text-emerald-300 font-semibold"><span>ROI sur apport</span><span>{scenarios.scenario_revente.roi_pct}%</span></div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-indigo-900/40 to-navy-800 rounded-lg p-3 border border-indigo-500/30" data-testid="scenario-location">
                          <div className="text-indigo-300 text-xs font-bold uppercase tracking-wide mb-2">🏠 Scénario Location LD</div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between text-white/80"><span>Loyer mensuel</span><span>{scenarios.scenario_location_ld.loyer_mensuel?.toLocaleString('fr-FR')} €</span></div>
                            <div className="flex justify-between text-white/70"><span>Loyer annuel</span><span>{scenarios.scenario_location_ld.loyer_annuel?.toLocaleString('fr-FR')} €</span></div>
                            <div className="flex justify-between text-white/60 text-xs italic"><span>Charges (22%)</span><span>-22%</span></div>
                            <div className="flex justify-between text-white/80"><span>Loyer net annuel</span><span>{scenarios.scenario_location_ld.loyer_net_annuel?.toLocaleString('fr-FR')} €</span></div>
                            <div className="flex justify-between text-white/80"><span>Mensualité crédit</span><span>-{scenarios.financing.monthly_payment?.toLocaleString('fr-FR')} €</span></div>
                            <div className="border-t border-white/10 my-1" />
                            <div className={`flex justify-between font-bold text-base ${scenarios.scenario_location_ld.cashflow_mensuel >= 0 ? 'text-gold' : 'text-red-300'}`}>
                              <span>Cashflow mensuel</span>
                              <span>{scenarios.scenario_location_ld.cashflow_mensuel >= 0 ? '+' : ''}{scenarios.scenario_location_ld.cashflow_mensuel?.toLocaleString('fr-FR')} €</span>
                            </div>
                            <div className="flex justify-between text-indigo-300 font-semibold"><span>Rentabilité nette</span><span>{scenarios.scenario_location_ld.rentabilite_nette}%</span></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* ============ PHASE 3/4/5 — ENRICHISSEMENT AUDIT ============ */}
                <Card className="bg-navy-700 border-white/10" data-testid="enrichment-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      📊 Enrichissement Audit
                      <span className="text-xs font-normal text-white/50">(MaPrimeRénov · Environnement · Obligations · DVF)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-white/70 text-[11px] mb-0.5 block">Revenu ménage</Label>
                        <select value={incomeTier} onChange={(e) => setIncomeTier(e.target.value)} className="w-full h-9 bg-white/10 border border-white/20 text-white rounded px-2 text-sm" data-testid="income-tier-select">
                          <option value="">—</option>
                          <option value="bleu">🔵 Très modeste</option>
                          <option value="jaune">🟡 Modeste</option>
                          <option value="violet">🟣 Intermédiaire</option>
                          <option value="rose">🌸 Supérieur</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-white/70 text-[11px] mb-0.5 block">Taille ménage</Label>
                        <Input type="number" value={householdSize} onChange={(e) => setHouseholdSize(parseInt(e.target.value) || 1)} className="h-9 bg-white/10 border-white/20 text-white text-sm" data-testid="household-size-input" />
                      </div>
                      <div>
                        <Label className="text-white/70 text-[11px] mb-0.5 block">DPE après travaux</Label>
                        <select value={dpeAfter} onChange={(e) => setDpeAfter(e.target.value)} className="w-full h-9 bg-white/10 border border-white/20 text-white rounded px-2 text-sm" data-testid="dpe-after-select">
                          {['A', 'B', 'C', 'D', 'E'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="flex items-center gap-1 text-[11px] text-white/80 cursor-pointer mt-1">
                          <input type="checkbox" checked={isCopro} onChange={(e) => setIsCopro(e.target.checked)} className="w-3.5 h-3.5 accent-amber-500" data-testid="is-copro-checkbox" />
                          Copropriété
                        </label>
                        <label className="flex items-center gap-1 text-[11px] text-white/80 cursor-pointer">
                          <input type="checkbox" checked={hasElecWork} onChange={(e) => setHasElecWork(e.target.checked)} className="w-3.5 h-3.5 accent-amber-500" data-testid="has-elec-checkbox" />
                          Travaux élec.
                        </label>
                      </div>
                    </div>
                    <Button onClick={fetchEnrichment} disabled={enrichmentLoading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="fetch-enrichment-btn">
                      {enrichmentLoading ? 'Analyse...' : '🚀 Enrichir l\'audit (MaPrimeRénov + Environnement + Obligations + DVF)'}
                    </Button>

                    {enrichment && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* MaPrimeRénov */}
                        {enrichment.maprimerenov && (
                          <div className="bg-gradient-to-br from-emerald-900/40 to-navy-800 rounded-lg p-3 border border-emerald-500/30" data-testid="enr-mpr">
                            <div className="text-emerald-300 text-xs font-bold uppercase tracking-wide mb-2">🔋 MaPrimeRénov'</div>
                            {enrichment.maprimerenov.eligible ? (
                              <div className="space-y-1 text-sm">
                                <div className="text-white/80 text-xs">{enrichment.maprimerenov.tier_label}</div>
                                <div className="text-white/70 text-xs italic">{enrichment.maprimerenov.parcours}</div>
                                <div className="flex justify-between text-white/80"><span>Gain DPE</span><span>{enrichment.maprimerenov.gain_classes} classe(s)</span></div>
                                <div className="flex justify-between text-white/80"><span>Taux aide</span><span>{enrichment.maprimerenov.aid_pct}%</span></div>
                                <div className="flex justify-between text-white/80"><span>Plafond travaux</span><span>{enrichment.maprimerenov.plafond_travaux?.toLocaleString('fr-FR')} €</span></div>
                                {enrichment.maprimerenov.bonus_passoire > 0 && (
                                  <div className="flex justify-between text-emerald-300"><span>Bonus sortie passoire</span><span>+{enrichment.maprimerenov.bonus_passoire} €</span></div>
                                )}
                                <div className="border-t border-white/10 my-1" />
                                <div className="flex justify-between text-gold font-bold text-base"><span>AIDE TOTALE</span><span>{enrichment.maprimerenov.total_aid?.toLocaleString('fr-FR')} €</span></div>
                                <div className="flex justify-between text-emerald-300 text-xs"><span>Éco-PTZ cumulable</span><span>jusqu'à {enrichment.maprimerenov.eco_ptz_eligible?.toLocaleString('fr-FR')} €</span></div>
                              </div>
                            ) : (
                              <div className="text-white/60 text-sm italic">Non éligible — {enrichment.maprimerenov.reason || 'Gain DPE insuffisant'}</div>
                            )}
                          </div>
                        )}

                        {/* Environnement */}
                        <div className="bg-gradient-to-br from-sky-900/40 to-navy-800 rounded-lg p-3 border border-sky-500/30" data-testid="enr-env">
                          <div className="text-sky-300 text-xs font-bold uppercase tracking-wide mb-2">🏫 Environnement (rayon 800m)</div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between text-white/80"><span>🏫 Écoles</span><span>{enrichment.environment.schools_count}{enrichment.environment.school_nearest_m ? ` · plus proche ${enrichment.environment.school_nearest_m}m` : ''}</span></div>
                            <div className="flex justify-between text-white/80"><span>🚉 Gares (rayon 2km)</span><span>{enrichment.environment.stations_count}{enrichment.environment.station_nearest_m ? ` · ${enrichment.environment.station_nearest_m}m` : ''}</span></div>
                            <div className="flex justify-between text-white/80"><span>🚌 Arrêts bus</span><span>{enrichment.environment.bus_stops_count}</span></div>
                            <div className="flex justify-between text-white/80"><span>🛒 Commerces</span><span>{enrichment.environment.shops_count}</span></div>
                            {(!enrichment.environment.schools_count && !enrichment.environment.stations_count) && (
                              <div className="text-amber-300 text-xs italic mt-1">⚠️ Coordonnées GPS manquantes sur ce dossier</div>
                            )}
                          </div>
                        </div>

                        {/* Obligations */}
                        <div className="bg-gradient-to-br from-amber-900/40 to-navy-800 rounded-lg p-3 border border-amber-500/30 md:col-span-1" data-testid="enr-obligations">
                          <div className="text-amber-300 text-xs font-bold uppercase tracking-wide mb-2">☑️ Obligations ({enrichment.obligations.length})</div>
                          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                            {enrichment.obligations.map((o, i) => (
                              <div key={i} className="text-xs bg-white/5 rounded px-2 py-1.5">
                                <div className="font-semibold text-white flex items-center gap-1.5">
                                  <span>{o.icon}</span>
                                  <span>{o.obligation}</span>
                                  <span className="ml-auto text-amber-300 text-[10px]">{o.cost}</span>
                                </div>
                                <div className="text-white/60 text-[11px] mt-0.5">{o.why}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* DVF analytics */}
                        <div className="bg-gradient-to-br from-purple-900/40 to-navy-800 rounded-lg p-3 border border-purple-500/30" data-testid="enr-dvf">
                          <div className="text-purple-300 text-xs font-bold uppercase tracking-wide mb-2">📈 DVF — Analyse secteur</div>
                          {enrichment.dvf_analytics.count > 0 ? (
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between text-white/80"><span>Ventes analysées</span><span>{enrichment.dvf_analytics.count}</span></div>
                              <div className="flex justify-between text-white/80"><span>Prix m² moyen</span><span>{enrichment.dvf_analytics.avg_price_m2?.toLocaleString('fr-FR')} €/m²</span></div>
                              <div className="flex justify-between text-white/80"><span>Prix m² médian</span><span>{enrichment.dvf_analytics.median_price_m2?.toLocaleString('fr-FR')} €/m²</span></div>
                              <div className="flex justify-between text-white/70 text-xs"><span>Fourchette</span><span>{enrichment.dvf_analytics.min_price_m2?.toLocaleString('fr-FR')} — {enrichment.dvf_analytics.max_price_m2?.toLocaleString('fr-FR')} €</span></div>
                              <div className="flex justify-between text-white/80"><span>Écart prix</span><span>{enrichment.dvf_analytics.spread_pct}%</span></div>
                              <div className="border-t border-white/10 my-1" />
                              <div className="flex justify-between text-gold font-bold"><span>Tension marché</span><span>{enrichment.dvf_analytics.market_tension}</span></div>
                            </div>
                          ) : (
                            <div className="text-white/60 text-sm italic">Pas de données DVF disponibles pour ce dossier</div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Work Estimates */}
                <Card className="bg-navy-700 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white">Estimation Travaux</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Quick Add Buttons */}
                    <div className="flex flex-wrap gap-2">
                      {WORK_PRESETS.map((preset) => (
                        <Button
                          key={preset.id}
                          data-testid={`add-work-${preset.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => addPresetWork(preset)}
                          className="border-white/20 text-white hover:bg-gold hover:text-black hover:border-gold text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {preset.label}
                        </Button>
                      ))}
                    </div>

                    {/* Work List - Editable */}
                    {workEstimates.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <div className="grid grid-cols-12 gap-2 text-xs text-white/50 px-3 pb-1 border-b border-white/10">
                          <span className="col-span-4">Désignation</span>
                          <span className="col-span-2 text-center">Prix unit.</span>
                          <span className="col-span-2 text-center">Qté</span>
                          <span className="col-span-1 text-center">Unité</span>
                          <span className="col-span-2 text-right">Total</span>
                          <span className="col-span-1"></span>
                        </div>
                        {workEstimates.map((work, index) => (
                          <div key={index} className="grid grid-cols-12 gap-2 items-center p-2 bg-white/5 rounded-lg">
                            <div className="col-span-4 flex items-center gap-1">
                              <Input
                                data-testid={`work-label-${index}`}
                                value={work.label}
                                onChange={(e) => updateWorkEstimate(index, 'label', e.target.value)}
                                className="flex-1 h-8 text-sm bg-white/10 border-white/20 text-white"
                              />
                              {work.fiche_pdf_url && (
                                <a
                                  href={work.fiche_pdf_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 hover:text-red-200"
                                  title={work.fiche_pdf_filename || 'Fiche technique PDF'}
                                  data-testid={`work-pdf-${index}`}
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                            <Input
                              data-testid={`work-price-${index}`}
                              type="number"
                              value={work.priceUnit}
                              onChange={(e) => updateWorkEstimate(index, 'priceUnit', e.target.value)}
                              className="col-span-2 h-8 text-sm bg-white/10 border-white/20 text-white text-center"
                            />
                            <Input
                              data-testid={`work-qty-${index}`}
                              type="number"
                              value={work.quantity}
                              onChange={(e) => updateWorkEstimate(index, 'quantity', e.target.value)}
                              className="col-span-2 h-8 text-sm bg-white/10 border-white/20 text-white text-center"
                            />
                            <select
                              data-testid={`work-unit-${index}`}
                              value={work.unit}
                              onChange={(e) => updateWorkEstimate(index, 'unit', e.target.value)}
                              className="col-span-1 h-8 text-xs bg-white/10 border border-white/20 text-white rounded-md"
                            >
                              <option value="forfait">forf.</option>
                              <option value="m²">m²</option>
                              <option value="ml">ml</option>
                              <option value="unité">u.</option>
                            </select>
                            <span className="col-span-2 text-right text-gold font-bold text-sm">
                              {formatPrice(work.amount)}
                            </span>
                            <Button
                              data-testid={`remove-work-${index}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => removeWork(index)}
                              className="col-span-1 h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        {/* Total Row */}
                        <div className="grid grid-cols-12 gap-2 items-center p-3 bg-gold/20 rounded-lg border border-gold/30">
                          <span className="col-span-9 text-white font-semibold">TOTAL TRAVAUX</span>
                          <span className="col-span-3 text-right text-gold font-bold text-lg">
                            {formatPrice(workEstimates.reduce((sum, w) => sum + (w.amount || 0), 0))}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Custom Work Input */}
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-sm text-white/60 mb-3">Ajouter une ligne personnalisée <span className="text-xs text-gold">— tapez 2+ caractères → choisis 🛒 Fourniture, 🔨 Pose, 📦 Total ou 📋 Détaillé (2 lignes)</span></p>
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-4 relative">
                          <Input
                            data-testid="custom-work-label"
                            placeholder="Désignation (ex: parquet, peinture...)"
                            value={customWork.label}
                            onChange={async (e) => {
                              const v = e.target.value;
                              setCustomWork({ ...customWork, label: v });
                              if (v.length >= 2) {
                                setSearchingProducts(true);
                                try {
                                  const r = await axios.get(`${API}/api/product-catalog/search?q=${encodeURIComponent(v)}&limit=8`);
                                  setProductSuggestions(r.data.products || []);
                                  setShowSuggestions(true);
                                } catch (err) { setProductSuggestions([]); }
                                setSearchingProducts(false);
                              } else {
                                setShowSuggestions(false);
                              }
                            }}
                            onFocus={() => { if (productSuggestions.length > 0) setShowSuggestions(true); }}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                            className="w-full bg-white/5 border-white/20 text-white placeholder:text-white/40"
                          />
                          {showSuggestions && productSuggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-2xl z-50 max-h-96 overflow-y-auto border border-gold/30" data-testid="product-suggestions">
                              {productSuggestions.map(p => {
                                const addFromCatalog = (mode) => {
                                  // mode: 'fourniture' | 'pose' | 'total' | 'detail'
                                  // PDF fiche produit = fiche MATÉRIEL → jointe uniquement aux lignes qui incluent du matériel
                                  // (fourniture, total, ligne fourniture du détaillé). JAMAIS sur une ligne "pose seule".
                                  const qty = parseFloat(customWork.quantity) || 1;
                                  const baseDesc = p.description || '';
                                  const productPdfUrl = p.fiche_pdf_url || '';
                                  const productPdfName = p.fiche_pdf_filename || '';
                                  if (mode === 'detail') {
                                    const f = parseFloat(p.prix_fourniture) || 0;
                                    const po = parseFloat(p.prix_pose) || 0;
                                    const lines = [];
                                    if (f > 0) lines.push({
                                      label: `${p.name} — Fourniture`,
                                      priceUnit: f,
                                      quantity: qty,
                                      unit: p.unit || 'forfait',
                                      amount: f * qty,
                                      description: baseDesc ? `Fourniture · ${baseDesc}` : 'Fourniture seule',
                                      product_id: p.id,
                                      fiche_pdf_url: productPdfUrl, // matériel → fiche OK
                                      fiche_pdf_filename: productPdfName,
                                    });
                                    if (po > 0) lines.push({
                                      label: `${p.name} — Pose`,
                                      priceUnit: po,
                                      quantity: qty,
                                      unit: p.unit || 'forfait',
                                      amount: po * qty,
                                      description: 'Main d\u2019œuvre / pose',
                                      product_id: p.id,
                                      fiche_pdf_url: '', // pose seule → PAS de fiche matériel
                                      fiche_pdf_filename: '',
                                    });
                                    if (lines.length === 0) {
                                      toast.error('Ce produit n\'a ni fourniture ni pose');
                                      return;
                                    }
                                    setWorkEstimates(prev => [...prev, ...lines]);
                                    toast.success(`${lines.length} ligne(s) ajoutée(s) (détaillé)`);
                                  } else {
                                    let priceUnit = 0;
                                    let suffix = '';
                                    let descMode = '';
                                    let attachPdf = false;
                                    if (mode === 'fourniture') { priceUnit = parseFloat(p.prix_fourniture) || 0; suffix = ' — Fourniture'; descMode = 'Fourniture seule'; attachPdf = true; }
                                    else if (mode === 'pose') { priceUnit = parseFloat(p.prix_pose) || 0; suffix = ' — Pose'; descMode = 'Main d\u2019œuvre / pose'; attachPdf = false; /* pose seule → PAS de fiche */ }
                                    else { priceUnit = parseFloat(p.prix_total) || 0; suffix = ''; descMode = 'Fourniture + Pose'; attachPdf = true; }
                                    if (priceUnit <= 0) { toast.error('Prix non défini pour ce mode'); return; }
                                    setWorkEstimates(prev => [...prev, {
                                      label: `${p.name}${suffix}`,
                                      priceUnit,
                                      quantity: qty,
                                      unit: p.unit || 'forfait',
                                      amount: priceUnit * qty,
                                      description: baseDesc ? `${descMode} · ${baseDesc}` : descMode,
                                      product_id: p.id,
                                      fiche_pdf_url: attachPdf ? productPdfUrl : '',
                                      fiche_pdf_filename: attachPdf ? productPdfName : '',
                                    }]);
                                    toast.success('Ligne ajoutée');
                                  }
                                  setCustomWork({ label: '', priceUnit: '', quantity: '', unit: 'forfait', description: '' });
                                  setShowSuggestions(false);
                                };
                                return (
                                  <div
                                    key={p.id}
                                    className="border-b border-gray-100 last:border-b-0 px-3 py-2 hover:bg-gold/5"
                                    onMouseDown={(e) => e.preventDefault()}
                                    data-testid={`suggestion-${p.id}`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-navy text-sm flex items-center gap-1.5">
                                          {p.name}
                                          {p.fiche_pdf_url && (
                                            <a
                                              href={p.fiche_pdf_url}
                                              target="_blank"
                                              rel="noreferrer"
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onClick={(e) => e.stopPropagation()}
                                              className="inline-flex items-center gap-0.5 text-red-600 hover:text-red-700 text-xs font-semibold"
                                              title={p.fiche_pdf_filename || 'Fiche technique PDF'}
                                              data-testid={`suggestion-pdf-${p.id}`}
                                            >
                                              <FileText className="w-3 h-3" /> PDF
                                            </a>
                                          )}
                                        </div>
                                        {p.description && (
                                          <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">{p.description}</div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {parseFloat(p.prix_fourniture) > 0 && (
                                        <button
                                          type="button"
                                          onMouseDown={(e) => { e.preventDefault(); addFromCatalog('fourniture'); }}
                                          className="text-[11px] px-2 py-1 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium"
                                          data-testid={`btn-fourniture-${p.id}`}
                                        >
                                          🛒 Fourniture {p.prix_fourniture}€
                                        </button>
                                      )}
                                      {parseFloat(p.prix_pose) > 0 && (
                                        <button
                                          type="button"
                                          onMouseDown={(e) => { e.preventDefault(); addFromCatalog('pose'); }}
                                          className="text-[11px] px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium"
                                          data-testid={`btn-pose-${p.id}`}
                                        >
                                          🔨 Pose {p.prix_pose}€
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onMouseDown={(e) => { e.preventDefault(); addFromCatalog('total'); }}
                                        className="text-[11px] px-2 py-1 rounded border border-emerald-300 bg-emerald-600 text-white hover:bg-emerald-700 font-semibold"
                                        data-testid={`btn-total-${p.id}`}
                                      >
                                        📦 Total {p.prix_total}€/{p.unit}
                                      </button>
                                      {parseFloat(p.prix_fourniture) > 0 && parseFloat(p.prix_pose) > 0 && (
                                        <button
                                          type="button"
                                          onMouseDown={(e) => { e.preventDefault(); addFromCatalog('detail'); }}
                                          className="text-[11px] px-2 py-1 rounded border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium"
                                          data-testid={`btn-detail-${p.id}`}
                                        >
                                          📋 Détaillé (2 lignes)
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <Input
                          data-testid="custom-work-price"
                          type="number"
                          placeholder="Prix €"
                          value={customWork.priceUnit}
                          onChange={(e) => setCustomWork({ ...customWork, priceUnit: e.target.value })}
                          className="col-span-2 bg-white/5 border-white/20 text-white placeholder:text-white/40"
                        />
                        <Input
                          data-testid="custom-work-qty"
                          type="number"
                          placeholder="Qté"
                          value={customWork.quantity}
                          onChange={(e) => setCustomWork({ ...customWork, quantity: e.target.value })}
                          className="col-span-2 bg-white/5 border-white/20 text-white placeholder:text-white/40"
                        />
                        <select
                          data-testid="custom-work-unit"
                          value={customWork.unit}
                          onChange={(e) => setCustomWork({ ...customWork, unit: e.target.value })}
                          className="col-span-2 bg-white/5 border border-white/20 text-white rounded-md"
                        >
                          <option value="forfait">forfait</option>
                          <option value="m²">m²</option>
                          <option value="ml">ml</option>
                          <option value="unité">unité</option>
                        </select>
                        <Button
                          data-testid="add-custom-work-btn"
                          onClick={addCustomWork}
                          className="col-span-2 bg-gold hover:bg-gold/90 text-black"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Financial Summary */}
                <Card className="bg-navy-700 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white">Bilan Financier</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-white/80">
                        <span>Prix d'achat</span>
                        <span>{formatPrice(selectedProject.purchase_price)}</span>
                      </div>

                      {/* ESTIMATION MARCHÉ DVF (juste sous le prix d'achat) */}
                      <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/30 rounded-lg p-3 mt-1 mb-2">
                        {marketLoading ? (
                          <div className="text-emerald-300 text-xs italic">Analyse du marché DVF en cours...</div>
                        ) : marketEstimation?.available ? (
                          <>
                            <div className="flex justify-between text-emerald-200 text-sm font-semibold">
                              <span>💡 Estimation marché DVF</span>
                              <span>~ {formatPrice(marketEstimation.estimated_price + (parseFloat(terrainAdjustment) || 0))}</span>
                            </div>
                            <div className="text-xs text-emerald-300/80 mt-1 space-y-0.5">
                              <div>📊 {marketEstimation.nb_transactions} ventes récentes analysées</div>
                              <div>📏 Prix/m² : {formatPrice(marketEstimation.price_per_m2)} × {selectedProject.surface} m²</div>
                              {marketEstimation.verdict && (
                                <div className={`font-semibold ${
                                  marketEstimation.verdict === 'SUR-évalué' ? 'text-red-400' :
                                  marketEstimation.verdict === 'SOUS-évalué' ? 'text-emerald-400' :
                                  'text-amber-400'
                                }`}>
                                  ⚖️ Écart client : {marketEstimation.ecart_pct > 0 ? '+' : ''}{marketEstimation.ecart_pct}% ({marketEstimation.verdict})
                                </div>
                              )}
                            </div>

                            {/* Bonus terrain (expert ajuste manuellement) */}
                            {selectedProject.land_surface > 0 && (
                              <div className="mt-3 pt-3 border-t border-emerald-500/20">
                                <div className="text-xs text-emerald-200/90 mb-2">
                                  🌳 Surface terrain client : <b>{selectedProject.land_surface} m²</b>
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="text-xs text-white/70 whitespace-nowrap">Ajustement terrain :</label>
                                  <input
                                    type="number"
                                    value={terrainAdjustment}
                                    onChange={(e) => setTerrainAdjustment(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder="0"
                                    className="flex-1 bg-navy-800 border border-emerald-500/40 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-emerald-400"
                                    data-testid="expert-terrain-adjustment-input"
                                  />
                                  <span className="text-white/70 text-sm">€</span>
                                </div>
                                <p className="text-[10px] text-white/50 mt-1 italic">Montant que vous ajoutez à l'estimation pour valoriser le terrain (0 si non applicable).</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-white/50 text-xs italic">
                            💡 Estimation marché DVF non disponible {marketEstimation?.reason ? `(${marketEstimation.reason})` : ''}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between text-white/80">
                        <span>Frais notaire ({(notaryRate * 100).toFixed(1)}%)</span>
                        <span>{formatPrice(notaryFees)}</span>
                      </div>
                      <div className="flex justify-between text-white/80">
                        <span>Travaux</span>
                        <span>{formatPrice(totalWorks)}</span>
                      </div>
                      <div className="flex justify-between text-white/80">
                        <span>Frais financiers (1%)</span>
                        <span>{formatPrice(financialFees)}</span>
                      </div>
                      
                      {/* Assurances Marchand */}
                      {isMarchand && totalWorks > 0 && (
                        <>
                          <div className="border-t border-amber-500/30 pt-2 mt-2">
                            <p className="text-amber-400 text-xs font-semibold mb-2">ASSURANCES MARCHAND</p>
                          </div>
                          <div className="flex justify-between text-amber-300/80 text-sm">
                            <span>Dommage-Ouvrage (2% travaux)</span>
                            <span>{formatPrice(insurances?.do || 0)}</span>
                          </div>
                          <div className="flex justify-between text-amber-300/80 text-sm">
                            <span>RC Pro Marchand</span>
                            <span>{formatPrice(insurances?.rcPro || 500)}</span>
                          </div>
                        </>
                      )}
                      
                      <div className="border-t border-white/20 pt-2 flex justify-between text-white font-bold">
                        <span>COÛT TOTAL</span>
                        <span className="text-gold">{formatPrice(totalCost)}</span>
                      </div>
                    </div>

                    {/* Tableau Comparatif Marchand */}
                    {isMarchand && (
                      <div className="bg-gradient-to-r from-emerald-500/10 to-amber-500/10 border border-emerald-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-emerald-400 font-semibold text-sm flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            OPTIMISATION MARCHAND
                          </h4>
                          <span className="text-xs bg-gold/20 text-gold px-2 py-1 rounded">Expertise 25 ans</span>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-white/60">Frais notaire Particulier (8%)</span>
                            <span className="text-red-400 line-through">{formatPrice(selectedProject.purchase_price * 0.08)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">Frais notaire Marchand (2.5%)</span>
                            <span className="text-emerald-400">{formatPrice(selectedProject.purchase_price * 0.025)}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-emerald-500/30">
                            <span className="text-emerald-400 font-semibold">Économie immédiate</span>
                            <span className="text-emerald-400 font-bold">{formatPrice(calculateMarchandSavings(selectedProject.purchase_price).savingsNotary)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-emerald-500/30">
                          <p className="text-xs text-white/50">
                            + TVA travaux récupérable sous régime sur marge • IS sur résultat réel vs 36,2% forfaitaire
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="border-t border-white/20 pt-4">
                      <Label className="text-white">Prix de Revente Estimé</Label>
                      <div className="relative mt-2">
                        <Input
                          data-testid="resale-price-expert"
                          type="number"
                          placeholder="Ex: 215000"
                          value={resalePrice}
                          onChange={(e) => setResalePrice(e.target.value)}
                          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 pr-10 text-lg h-12"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40">€</span>
                      </div>
                    </div>

                    {resalePrice && (
                      <div className={`p-4 rounded-lg ${netMargin > 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-white font-medium">MARGE NETTE</span>
                          <div className="text-right">
                            <span className={`text-2xl font-bold ${netMargin > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatPrice(netMargin)}
                            </span>
                            <span className={`ml-2 text-sm ${netMargin > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              ({marginPercent}%)
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Note Marchand en bas de page */}
                    {isMarchand && (
                      <div className="text-xs text-white/40 italic border-t border-white/10 pt-3">
                        Bilan établi en tenant compte des obligations d'assurances professionnelles liées au régime Marchand de Biens (DO, RC Pro).
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Ajustements Expert */}
                <Card className="bg-navy-700 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Ajustements Expert
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-sm text-white/70 mb-4">
                      Ajoutez des ajustements pour justifier votre note. Ces modifications impactent les calculs financiers (marge, ROI, cashflow).
                    </div>

                    {/* Liste des ajustements existants */}
                    {expertAdjustments.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-white">Ajustements actuels</Label>
                        {expertAdjustments.map((adj, index) => (
                          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="text-white font-medium text-sm">{adj.label}</div>
                              <div className="text-white/60 text-xs mt-1">
                                {adj.type === 'valorisation' ? '💰 Valorisation' : '🔧 Travaux'} • 
                                <span className={adj.amount > 0 ? 'text-emerald-400' : 'text-red-400'}>
                                  {adj.amount > 0 ? '+' : ''}{adj.amount.toLocaleString('fr-FR')} €
                                </span>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeAdjustment(index)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Ajouter un nouvel ajustement */}
                    <div className="space-y-3 border-t border-white/10 pt-4">
                      <Label className="text-white">Nouvel ajustement</Label>
                      <div className="space-y-2">
                        <Input
                          placeholder="Description (ex: Cuisine rénovée haut de gamme)"
                          value={newAdjustment.label}
                          onChange={(e) => setNewAdjustment({ ...newAdjustment, label: e.target.value })}
                          className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                        />
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            placeholder="Montant (€)"
                            value={newAdjustment.amount}
                            onChange={(e) => setNewAdjustment({ ...newAdjustment, amount: e.target.value })}
                            className="flex-1 bg-white/5 border-white/20 text-white placeholder:text-white/40"
                          />
                          <select
                            value={newAdjustment.type}
                            onChange={(e) => setNewAdjustment({ ...newAdjustment, type: e.target.value })}
                            className="bg-white/5 border border-white/20 text-white rounded-md px-3 py-2"
                          >
                            <option value="valorisation">💰 Valorisation</option>
                            <option value="travaux">🔧 Travaux</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={addAdjustment}
                          className="flex-1 bg-gold/20 hover:bg-gold/30 text-gold border border-gold/50"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter
                        </Button>
                        <Button
                          onClick={saveAdjustments}
                          disabled={savingAdjustments || expertAdjustments.length === 0}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {savingAdjustments ? 'Sauvegarde...' : 'Sauvegarder & Recalculer'}
                        </Button>
                      </div>
                      <div className="text-xs text-white/50">
                        💡 Astuce : Valorisation = augmente la valeur de revente • Travaux = augmente le coût total
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Validation */}
                <Card className="bg-navy-700 border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white">Validation Expert</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-white mb-3 block">Score de l'affaire</Label>
                      <div className="flex gap-3">
                        <Button
                          data-testid="score-green"
                          onClick={() => handleScoreChange('green')}
                          className={`flex-1 ${selectedScore === 'green' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-white/10 hover:bg-emerald-500/50'} text-white`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Validé
                        </Button>
                        <Button
                          data-testid="score-orange"
                          onClick={() => handleScoreChange('orange')}
                          className={`flex-1 ${selectedScore === 'orange' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-white/10 hover:bg-amber-500/50'} text-white`}
                        >
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Risqué
                        </Button>
                        <Button
                          data-testid="score-red"
                          onClick={() => handleScoreChange('red')}
                          className={`flex-1 ${selectedScore === 'red' ? 'bg-red-500 hover:bg-red-600' : 'bg-white/10 hover:bg-red-500/50'} text-white`}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Refusé
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="notes" className="text-white">Notes de l'expert</Label>
                      {/* Bouton charger rapport complet */}
                      <Button
                        data-testid="load-full-report-btn"
                        onClick={loadFullReportInNotes}
                        disabled={loadingFullReport}
                        variant="outline"
                        className="mt-1 mb-1 w-full border-gold/40 text-gold hover:bg-gold/10 font-semibold text-sm"
                      >
                        {loadingFullReport
                          ? 'Chargement...'
                          : '📋 Charger TOUT le contenu PDF ici (toutes sections modifiables)'}
                      </Button>
                      <Textarea
                        id="notes"
                        data-testid="expert-notes"
                        placeholder="Cliquez sur 'Charger TOUT le contenu PDF' pour voir et modifier toutes les sections (Checklist, DVF, Alertes, Finances, Travaux, Glossaire...)"
                        value={expertNotes}
                        onChange={(e) => setExpertNotes(e.target.value)}
                        rows={20}
                        style={{ minHeight: '400px' }}
                        className="mt-2 bg-white/5 border-white/20 text-white placeholder:text-white/40 text-sm leading-relaxed font-mono"
                      />
                    </div>

                    <div className="flex flex-col gap-2 pt-2">
                      <div className="flex gap-3 flex-wrap">
                        <Button
                          data-testid="validate-project-btn"
                          onClick={handleValidate}
                          disabled={calculating}
                          className="flex-1 bg-gold hover:bg-gold/90 text-black font-semibold"
                        >
                          {calculating ? 'Validation...' : 'Valider le dossier'}
                        </Button>
                        <Button
                          data-testid="generate-pdf-btn"
                          onClick={generatePdf}
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          PDF simple
                        </Button>
                        <Button
                          data-testid="audit-report-pdf-btn"
                          onClick={downloadAuditReport}
                          disabled={downloadingAuditPdf}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          {downloadingAuditPdf ? 'Génération...' : '📄 Rapport audit complet'}
                        </Button>
                        <Button
                          data-testid="delete-project-btn"
                          onClick={handleDeleteProject}
                          variant="outline"
                          className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer dossier
                        </Button>
                      </div>
                      <div className="flex items-center justify-between gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                        <div className="flex-1 min-w-0">
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={includeFiches}
                              onChange={(e) => setIncludeFiches(e.target.checked)}
                              className="w-4 h-4 accent-emerald-500"
                              data-testid="include-fiches-checkbox"
                            />
                            <span className="text-sm text-white/90">
                              Joindre les fiches PDF produits
                              {(() => {
                                const n = workEstimates.filter(w => w.fiche_pdf_url).length;
                                return n > 0 ? <span className="ml-1 text-emerald-300 font-semibold">({n} dispo{n > 1 ? 'nibles' : 'nible'})</span> : <span className="ml-1 text-white/50 text-xs">(aucune attachée)</span>;
                              })()}
                            </span>
                          </label>
                        </div>
                        <Button
                          data-testid="send-devis-to-client-btn"
                          onClick={sendDevisToClient}
                          disabled={sendingDevis}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex-shrink-0"
                        >
                          {sendingDevis ? 'Envoi...' : '📧 Envoyer au client'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </main>
        ) : (
          <main className="flex-1 hidden md:flex items-center justify-center">
            <div className="text-center">
              <FolderOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">Sélectionnez un dossier dans la liste</p>
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
